<template>
  <b-overlay :show="isLoading || selectAllTradiesBusy" rounded="sm">
    <template #overlay>
      <div class="text-center">
        <digger-loader />
      </div>
    </template>
    <transition name="bounce" @after-enter="positionLockOverlays">
      <b-container class="py-5 custom-container container">
        <b-container class="custom-container mt-5">
          <b-breadcrumb
            :items="breadcrumbs"
            class="pl-0 pt-5 mt-5 bg-transparent"
          ></b-breadcrumb>

          <!-- ROW -->
          <b-row class="project-header-row">
            <b-col class="project-title-col">
              <h2 class="font-weight-bold project-title">
                {{ project ? project.name : '' }}
              </h2>
            </b-col>

            <b-col class="text-right align-right project-actions-col">
              <b-row
                class="justify-content-end flex-nowrap flex-md-wrap align-items-center"
              >
                <b-col
                  v-if="canDeleteProject"
                  cols="auto"
                  md="4"
                  class="px-1 px-md-2 mb-2 mb-md-0"
                >
                  <b-button
                    variant="outline-secondary"
                    class="bg-white text-danger header-action-btn"
                    title="Delete"
                    @click="deletingProject(true)"
                  >
                    <b-icon-trash
                      class="header-action-icon d-md-none"
                      aria-hidden="true"
                    />
                    <span class="d-none d-md-inline">Delete</span>
                    <span class="sr-only d-md-none">Delete</span>
                  </b-button>
                </b-col>
                <b-col
                  v-if="canEditProject"
                  cols="auto"
                  md="4"
                  class="px-1 px-md-2 mb-2 mb-md-0"
                >
                  <b-button
                    variant="outline-secondary"
                    class="bg-white text-dark header-action-btn"
                    title="Edit"
                    @click="editingProject(true)"
                  >
                    <b-icon-pencil
                      class="header-action-icon d-md-none"
                      aria-hidden="true"
                    />
                    <span class="d-none d-md-inline">Edit</span>
                    <span class="sr-only d-md-none">Edit</span>
                  </b-button>
                </b-col>
                <b-col cols="auto" md="4" class="px-1 px-md-2 mb-2 mb-md-0">
                  <b-button
                    variant="outline-secondary"
                    class="bg-white text-dark header-action-btn"
                    title="Back to Projects"
                    @click="gotoProjects()"
                  >
                    <b-icon-arrow-left
                      class="header-action-icon d-md-none"
                      aria-hidden="true"
                    />
                    <span class="d-none d-md-inline">Back to Projects</span>
                    <span class="sr-only d-md-none">Back to Projects</span>
                  </b-button>
                </b-col>

                <b-modal
                  v-model="isDeletingProject"
                  title="Delete Project"
                  centered
                  hide-header
                  hide-footer
                >
                  <div class="delete-project-modal text-center">
                    <div class="delete-project-modal__icon">
                      <b-icon-trash />
                    </div>
                    <h4 class="font-weight-bold mb-2">Delete Project</h4>
                    <p class="text-muted mb-1">
                      Are you sure you want to delete
                      <span class="font-weight-bold text-dark">{{
                        project ? project.name : ''
                      }}</span
                      >?
                    </p>
                    <p class="text-danger small mb-4">
                      This action can’t be undone.
                    </p>

                    <div class="delete-project-modal__actions">
                      <b-button
                        variant="outline-secondary"
                        class="delete-project-modal__btn"
                        @click="deletingProject(false)"
                      >
                        Cancel
                      </b-button>
                      <b-button
                        variant="danger"
                        class="delete-project-modal__btn"
                        @click="onDeleteProject()"
                      >
                        <b-icon-trash class="mr-1" />
                        Delete
                      </b-button>
                    </div>
                  </div>
                </b-modal>

                <b-modal
                  v-if="cProject"
                  v-model="isEditingProject"
                  title="Edit Project"
                  centered
                  :hide-footer="true"
                >
                  <b-form @submit.prevent="onUpdateProject">
                    <b-form-group label="Project Name">
                      <b-form-input
                        v-model="cProject.name"
                        placeholder="Enter Project Name"
                        required
                      ></b-form-input>
                    </b-form-group>

                    <div class="edit-project-actions">
                      <b-button
                        variant="outline-secondary"
                        class="edit-project-actions__btn"
                        @click="editingProject(false)"
                      >
                        Cancel
                      </b-button>
                      <b-button
                        type="submit"
                        variant="primary"
                        class="edit-project-actions__btn"
                      >
                        Update
                      </b-button>
                    </div>
                  </b-form>
                </b-modal>
              </b-row>
            </b-col>
          </b-row>

          <!-- ROW -->
          <b-row>
            <b-col cols="12" sm="12" class="d-flex flex-column">
              <b-container fluid>
                <!-- Overview | Files. The project's own documents used to sit
                     ~5 screens down, below every tradie section. -->
                <project-page-tabs
                  class="mt-4"
                  :active="activeTab"
                  :showFiles="canViewProjectFiles"
                  :showJobs="canViewProjectJobs"
                  @change="setTab"
                />

                <b-card
                  class="file-details"
                  :class="[
                    canViewProjectFiles ? 'mt-3' : 'mt-4',
                    { 'd-none': !showOverviewTab },
                  ]"
                >
                  <h3 class="my-4 font-weight-bold">Project Details</h3>
                  <!-- b-card already wraps its default slot in its own
                       implicit .card-body (~20px padding all sides); this
                       explicit, nested b-card-body stacks a second card-
                       body's worth of padding on top of that. pl-0 zeroed
                       only its left side (so the left edge lines up with the
                       "Project Details" h3 above, which only has the outer
                       card-body's padding) — leaving right at its default
                       and stacking with the outer's right padding for ~40px
                       total vs ~20px on the left. That's the root of every
                       section below (Tradies, Needing Attention, Expired
                       Requests, Expiring Files) sitting off-centre in the
                       card. pr-0 makes it symmetric everywhere. -->
                  <b-card-body class="pl-0 pr-0 align-items-center">
                    <!-- Project Name -->
                    <b-row class="mb-4 project-name-detail-row" no-gutters>
                      <!-- eslint-disable-next-line -->
                      <b-col cols="auto" class="font-weight-bold text-nowrap project-name-detail-label">Project</b-col>
                      <b-col class="gray--2 project-name-detail-value">
                        {{ project ? project.name : '' }}
                      </b-col>
                    </b-row>

                    <!-- Tradies -->
                    <b-row class="tradie-row">
                      <!-- col-md-auto/col-md (label beside content at md+)
                           put the table an extra label-width further right
                           than the other three sections, which stack their
                           label above the content at every breakpoint —
                           that's what made this row's left gap so much
                           bigger than its right gap once matching padding
                           was added. Below md this row already wraps/stacks
                           on its own (4 + 12 > 12 cols), so dropping the
                           md-only auto/fill classes just makes that the
                           behaviour at every breakpoint, matching the other
                           three and keeping the existing mobile-tuned
                           .tradie-search/.tradie-toolbar-* rules (already
                           built around a stacked label) unaffected. -->
                      <!-- eslint-disable-next-line -->
                      <b-col cols="4" sm="4" class="font-weight-bold text-nowrap section-title">Tradies:</b-col>
                      <b-col cols="12" class="tradie-content-col">
                        <b-container fluid class="px-0">
                          <b-row
                            align-h="end"
                            align-v="end"
                            class="tradie-toolbar-row"
                          >
                            <!-- <b-col
                              cols="3"
                              sm="6"
                              md="4"
                              lg="3"
                              class="p-3 mb-0"
                            >
                            </b-col> -->
                            <b-col
                              cols="12"
                              md="4"
                              class="justify-content-start align-items-center mb-3 pr-md-5 tradie-search"
                            >
                              <b>Search (Name, Email, Role):</b>
                              <b-form-input
                                v-model="search"
                                placeholder="Type to search"
                                debounce="500"
                              />
                            </b-col>
                            <b-col
                              class="d-flex flex-wrap justify-content-end align-items-end tradie-toolbar-actions"
                              :class="{
                                'tradie-toolbar-actions--supervisor':
                                  isSupervisorTradie,
                              }"
                            >
                              <b-button
                                v-if="canAddTradies"
                                variant="primary"
                                style="white-space: nowrap"
                                class="px-3 py-0 mb-3 mr-3 tradie-action-btn"
                                @click="addingTradie(true)"
                              >
                                <b-icon-plus
                                  font-scale="1.5"
                                  class="align-middle"
                                />
                                <span class="d-none d-lg-inline">
                                  Add Tradie
                                </span>
                                <span class="d-lg-none">Add</span>
                              </b-button>

                              <b-button
                                v-if="canShareTradies"
                                class="px-3 py-0 mb-3 mr-3 tradie-action-btn"
                                style="white-space: nowrap"
                                variant="dark"
                                @click="shareSelectedTradies(true)"
                              >
                                <b-icon-plus
                                  font-scale="1.5"
                                  class="align-middle"
                                ></b-icon-plus>
                                <span class="d-none d-lg-inline">
                                  Share Tradies
                                </span>
                                <span class="d-lg-none">Share</span>
                              </b-button>

                              <b-button
                                v-if="canDownloadTradieFiles"
                                class="px-3 py-0 mb-3 mr-3 tradie-action-btn"
                                style="white-space: nowrap"
                                @click="downloadFile"
                              >
                                <b-icon-download class="align-middle" />
                                <span class="align-middle d-none d-lg-inline">
                                  Download Tradies
                                </span>
                                <span class="align-middle d-lg-none">
                                  Download
                                </span>
                              </b-button>

                              <b-button
                                v-if="canExportMatrix"
                                class="px-3 py-0 mb-3 mr-3 tradie-action-btn"
                                style="white-space: nowrap"
                                @click="downloadTicketsCsv"
                                variant="success"
                              >
                                <b-icon-download class="align-middle" />
                                <span class="align-middle d-none d-lg-inline">
                                  Export Tradie Matrix
                                </span>
                                <span class="align-middle d-lg-none"
                                  >Export</span
                                >
                              </b-button>
                            </b-col>
                            <!-- <b-button
                              variant="success"
                              style="height: 32px"
                              class="px-3 py-0 mb-3"
                              @click="importingTradieCSV(true)"
                              ><b-icon-plus
                                font-scale="1.5"
                                class="align-middle"
                              />
                              <span>Import tradies</span>
                            </b-button> -->
                          </b-row>
                        </b-container>

                        <!-- Add Tradie -->
                        <b-modal
                          v-model="isAddingTradie"
                          title="Add Tradie"
                          centered
                          :hide-footer="true"
                          size="xl"
                        >
                          <project-tradies-list
                            class="mb-4"
                            v-on:tradieSelected="onTradieSelected"
                            :projectId="projectId"
                          />
                          <!-- :projectId="project ? project.id : null" -->
                          <div class="add-tradie-actions">
                            <b-button
                              variant="outline-secondary"
                              class="add-tradie-actions__btn"
                              @click="addingTradie(false)"
                            >
                              Cancel
                            </b-button>
                            <b-button
                              type="submit"
                              variant="primary"
                              class="add-tradie-actions__btn"
                              @click="onAddTradie"
                            >
                              <b-icon-plus class="mr-1" />
                              <span class="d-none d-sm-inline">Add Tradie</span>
                              <span class="d-inline d-sm-none">Add</span>
                            </b-button>
                          </div>
                        </b-modal>

                        <!-- Import tradies (CSV or Excel) -->
                        <b-modal
                          v-model="isImportingTradieCSV"
                          title="Import tradies"
                          centered
                          :hide-footer="true"
                        >
                          <b-overlay :show="isLoading" rounded="sm">
                            <template #overlay>
                              <div class="text-center">
                                <digger-loader />
                              </div>
                            </template>
                            <b-form @submit.prevent="onImportTradieCSV">
                              <b-form-row>
                                <b-col>
                                  <b-form-group
                                    label="CSV or Excel file (.csv, .xlsx)"
                                  >
                                    <b-form-file
                                      v-model="tradieCsvFile"
                                      :accept="tradieImportAccept"
                                      class="felix"
                                      required
                                      @change="onTradieImportFileChange"
                                    />
                                    <p
                                      v-if="tradieImportFileClientHint"
                                      class="text-danger small mb-1"
                                    >
                                      {{ tradieImportFileClientHint }}
                                    </p>
                                    <p class="text-black opacity-100">
                                      Download:
                                      <a href="/tradiepass_tradies.csv">
                                        Tradies template (CSV)
                                      </a>
                                    </p>
                                  </b-form-group>
                                </b-col>
                              </b-form-row>

                              <b-row align-h="end" class="p-2">
                                <b-button
                                  variant="outline-secondary"
                                  class="mx-2"
                                  @click="importingTradieCSV(false)"
                                >
                                  CANCEL
                                </b-button>
                                <b-button
                                  type="submit"
                                  variant="primary"
                                  class="mx-2"
                                >
                                  UPLOAD
                                </b-button>
                              </b-row>
                            </b-form>
                          </b-overlay>
                        </b-modal>

                        <TradieCsvImportErrorModal
                          v-model="showImportCsvErrorModal"
                          :variant="importCsvErrorModalVariant"
                          :summary="importCsvErrorSummary"
                          :emails="importCsvErrorEmails"
                          :skipped-already-in-company="
                            importCsvErrorSkippedAlreadyInCompany
                          "
                          :skipped-business-admins="
                            importCsvErrorSkippedBusinessAdmins
                          "
                          :imported-emails="importCsvErrorImportedEmails"
                          :invited-emails="importCsvErrorInvitedEmails"
                        />

                        <!-- Share Tradies -->
                        <ShareTradiesModal
                          v-model="isShareSelectedTradies"
                          :selected-tradies="selectedTradies"
                        />

                        <!-- Mobile: stacked tradie rows (hidden on md+) -->
                        <div class="d-md-none mobile-tradie-list">
                          <div
                            v-for="item in visibleTradies"
                            :key="item.id"
                            class="mobile-tradie-item"
                            :class="[
                              getRowClass(item),
                              { 'is-selected': isTradieSelected(item.id) },
                            ]"
                            @click="viewTradie(item)"
                          >
                            <b-icon-check-circle-fill
                              v-if="item.project_approved"
                              class="text-success mobile-tradie__tick"
                              title="Project approved"
                            />
                            <div class="mobile-tradie__body">
                              <span
                                class="mobile-tradie__name"
                                :class="{
                                  'text-muted': isTradieTokenLocked(item),
                                }"
                              >
                                {{
                                  item.full_name === ''
                                    ? '-----'
                                    : item.full_name
                                }}
                              </span>
                              <div
                                v-if="canShowTradieField(item)"
                                class="mobile-tradie__meta"
                              >
                                <span v-if="item.email">{{ item.email }}</span>
                                <span
                                  v-if="
                                    item.trade_names && item.trade_names.length
                                  "
                                >
                                  · {{ shortenString(item.trade_names, 20) }}
                                </span>
                                <span
                                  v-if="item.need_attention"
                                  class="text-danger"
                                >
                                  · Needs attention
                                </span>
                              </div>
                              <div
                                v-else
                                class="mobile-tradie__meta mobile-tradie__meta--lock d-flex align-items-center"
                              >
                                <img
                                  src="@/assets/images/lock.png"
                                  alt="Locked"
                                  class="mobile-tradie__lock-icon mr-1"
                                />
                                {{ tradieLockMessage() }}
                              </div>
                            </div>
                            <div
                              v-if="canShowTradieField(item)"
                              class="mobile-tradie__select"
                              @click.stop
                            >
                              <b-button
                                :style="{
                                  backgroundColor: isTradieSelected(item.id)
                                    ? '#28a745'
                                    : 'white',
                                }"
                                class="mobile-tradie__select-btn"
                                @click="toggleTradieSelection(item.id)"
                              >
                                <b-icon-check2
                                  v-if="isTradieSelected(item.id)"
                                />
                              </b-button>
                            </div>
                            <div
                              v-if="canRemoveTradies"
                              class="mobile-tradie__remove"
                              @click.stop
                            >
                              <b-button
                                variant="outline-light"
                                class="mobile-tradie__remove-btn"
                                @click="removingTradie(true, item.id)"
                              >
                                <b-icon-x-circle-fill variant="danger" />
                              </b-button>
                            </div>
                          </div>
                          <div
                            v-if="!visibleTradies || !visibleTradies.length"
                            class="text-center py-4 text-muted"
                          >
                            No tradies found.
                          </div>
                        </div>

                        <!-- Tradies Table (desktop) -->
                        <div
                          class="table-wrapper d-none d-md-block"
                          ref="tableWrapper"
                        >
                          <b-table
                            v-if="tradies"
                            :items="visibleTradies"
                            :fields="tradieFields"
                            show-empty
                            responsive
                            hover
                            :tbody-tr-class="getRowClass"
                            class="tradies-table"
                            @row-clicked="viewTradie"
                          >
                            <template #cell(project_approved)="{ item }">
                              <b-icon-check-circle-fill
                                v-if="item.project_approved"
                                v-b-tooltip.hover
                                title="Project approved"
                                class="text-success project-approved-tick"
                                aria-label="Project approved"
                              />
                            </template>

                            <template #cell(full_name)="data">
                              <div class="cell-content-wrapper">
                                <span
                                  :class="{
                                    'text-muted': isTradieTokenLocked(
                                      data.item,
                                    ),
                                  }"
                                >
                                  {{
                                    data.item.full_name == ''
                                      ? '-----'
                                      : data.item.full_name
                                  }}
                                </span>
                                <div
                                  v-if="isTradieTokenLocked(data.item)"
                                  class="lock-overlay"
                                  :ref="`lock-over-lay-${data.item.id}`"
                                >
                                  <img
                                    src="@/assets/images/lock.png"
                                    alt="Locked"
                                    class="lock-icon mr-2"
                                  />
                                  <span>{{ tradieLockMessage() }}</span>
                                </div>
                              </div>
                            </template>

                            <template #cell(trade_names)="data">
                              <span
                                v-if="canShowTradieField(data.item)"
                                v-b-tooltip.html
                                :title="getTooltipText(data.item.trade_names)"
                                variant="outline-success"
                              >
                                {{ shortenString(data.item.trade_names, 30) }}
                              </span>
                            </template>

                            <template #cell(proof_of_id_files)="data">
                              <span
                                v-if="canShowTradieField(data.item)"
                                v-b-tooltip.html
                                :title="
                                  getTooltipText(data.item.proof_of_id_files)
                                "
                                variant="outline-success"
                              >
                                {{ shortenString(data.item.proof_of_id_files) }}
                              </span>
                            </template>

                            <template #cell(need_attention)="{ item }">
                              <span v-if="canShowTradieField(item)">
                                {{ item.need_attention ? 'Yes' : 'No' }}
                              </span>
                            </template>

                            <template #cell(token_expiry)="{ item }">
                              <div v-if="canShowTradieField(item)">
                                <span v-if="tokenExpiryDates[item.id]">
                                  <span
                                    :class="{
                                      'text-danger font-weight-bold':
                                        isExpiringSoon(
                                          tokenExpiryDates[item.id],
                                          30,
                                        ),
                                      'text-warning':
                                        isExpiringSoon(
                                          tokenExpiryDates[item.id],
                                          60,
                                        ) &&
                                        !isExpiringSoon(
                                          tokenExpiryDates[item.id],
                                          30,
                                        ),
                                    }"
                                  >
                                    {{
                                      formatExpiryDate(
                                        tokenExpiryDates[item.id],
                                      )
                                    }}
                                  </span>
                                </span>
                                <span
                                  v-else-if="tokenExpiryDates[item.id] === null"
                                  class="text-muted"
                                  style="font-size: 0.85rem"
                                  >-</span
                                >
                                <span
                                  v-else
                                  class="text-muted"
                                  style="font-size: 0.85rem"
                                  >Loading...</span
                                >
                              </div>
                              <span v-else-if="isTradieTokenLocked(item)"
                                >-</span
                              >
                            </template>

                            <template #cell(actions)="{ item }">
                              <b-button
                                v-if="canRemoveTradies"
                                variant="outline-light"
                                style="height: 32px"
                                class="px-2"
                                @click="removingTradie(true, item.id)"
                              >
                                <b-icon-x-circle-fill variant="danger" />
                              </b-button>
                            </template>
                            <template #head(select)>
                              <div class="text-center w-100">
                                <span
                                  :style="{
                                    cursor: selectAllTradiesBusy
                                      ? 'wait'
                                      : 'pointer',
                                    'user-select': 'none',
                                    padding: '2px 6px',
                                    border: '1px solid #ddd',
                                    'border-radius': '3px',
                                    display: 'inline-block',
                                    opacity: selectAllTradiesBusy ? 0.5 : 1,
                                  }"
                                  @click="
                                    !selectAllTradiesBusy &&
                                      toggleSelectAllTradies()
                                  "
                                >
                                  Select All
                                </span>
                              </div>
                            </template>

                            <template #cell(select)="{ item }">
                              <div
                                class="text-center d-flex justify-content-center align-items-center"
                              >
                                <b-button
                                  v-if="canShowTradieField(item)"
                                  :style="{
                                    backgroundColor: isTradieSelected(item.id)
                                      ? '#28a745'
                                      : 'white',
                                  }"
                                  style="
                                    height: 32px;
                                    width: 32px;
                                    border-radius: 8px;
                                    border: 1px solid grey;
                                    padding-left: 5px !important;
                                  "
                                  class="px-2"
                                  @click="toggleTradieSelection(item.id)"
                                >
                                  <b-icon-check2
                                    v-if="isTradieSelected(item.id)"
                                  />
                                </b-button>
                              </div>
                            </template>
                          </b-table>
                        </div>

                        <b-pagination
                          v-if="rows > perPage"
                          v-model="currentPage"
                          :total-rows="rows"
                          :per-page="perPage"
                          :current-page="currentPage"
                          :limit="paginationLimit"
                          aria-controls="my-table"
                          align="center"
                          class="tradies-pagination"
                        />

                        <p v-if="tradies" align="center" class="tradie-count">
                          Showing {{ tradies.length }} out of total of
                          {{ rows }} Tradies
                        </p>

                        <!-- Remove Tradie -->
                        <b-modal
                          v-model="isRemovingTradie"
                          title="Remove Tradie from this Project"
                          modal-class="remove-project-tradie-modal"
                          centered
                        >
                          <p class="remove-project-tradie-text">
                            Are you sure to remove <b>"{{ tradieName }}"</b>?
                          </p>

                          <template #modal-footer>
                            <div class="remove-project-tradie-actions">
                              <b-button
                                variant="secondary"
                                class="remove-project-tradie-btn"
                                @click="removingTradie(false)"
                              >
                                CANCEL
                              </b-button>
                              <b-button
                                variant="primary"
                                class="remove-project-tradie-btn"
                                @click="onRemoveTradie()"
                              >
                                YES
                              </b-button>
                            </div>
                          </template>
                        </b-modal>
                      </b-col>
                    </b-row>

                    <template v-if="attentionTradiesAll.length > 0">
                      <!-- Separator -->
                      <div class="section-separator"></div>

                      <!-- Tradies Needing Attention -->

                      <b-row class="bg-highlight py-4 attention-row">
                        <!-- Label sits on its own full-width row above the
                             table instead of squeezing beside it, so the
                             table itself gets the full row width. Global
                             .col-12 rule (_animations.scss) already adds a
                             30px margin-bottom below it. -->
                        <b-col cols="12" class="font-weight-bold section-title">
                          Needing Attention:
                        </b-col>
                        <b-col cols="12" class="attention-cards-col">
                          <!-- Independent of the Tradies table's own search
                               box above — filters only this band. -->
                          <b-row>
                            <b-col
                              cols="12"
                              md="4"
                              class="mb-3 tradie-search band-search"
                            >
                              <b>Search (Name, Email, Role):</b>
                              <b-form-input
                                v-model="attentionSearch"
                                placeholder="Type to search"
                                debounce="500"
                              />
                            </b-col>
                          </b-row>

                          <!-- Mobile: stacked tradie rows (hidden on md+) -->
                          <div class="d-md-none mobile-tradie-list">
                            <div
                              v-for="item in attentionTradiesPage"
                              :key="item.id"
                              class="mobile-tradie-item"
                              :class="getRowClass(item)"
                              @click="viewTradie(item)"
                            >
                              <div class="mobile-tradie__body">
                                <div class="mobile-tradie__name-row">
                                  <span
                                    class="mobile-tradie__name"
                                    :class="{
                                      'text-muted': isTradieTokenLocked(item),
                                    }"
                                  >
                                    {{
                                      item.full_name === ''
                                        ? '-----'
                                        : item.full_name
                                    }}
                                  </span>
                                  <b-icon-check-circle-fill
                                    v-if="item.project_approved"
                                    class="text-success mobile-tradie__tick"
                                    title="Project approved"
                                  />
                                </div>
                                <div
                                  v-if="canShowTradieField(item)"
                                  class="mobile-tradie__meta"
                                >
                                  <span v-if="item.email">{{
                                    item.email
                                  }}</span>
                                  <span
                                    v-if="
                                      item.trade_names &&
                                      item.trade_names.length
                                    "
                                  >
                                    · {{ shortenString(item.trade_names, 20) }}
                                  </span>
                                </div>
                                <ul
                                  v-if="
                                    canShowTradieField(item) &&
                                    needAttentionReasons(item).length
                                  "
                                  class="mobile-tradie__reasons"
                                >
                                  <li
                                    v-for="(reason, i) in needAttentionReasons(
                                      item,
                                    )"
                                    :key="i"
                                  >
                                    {{ reason }}
                                  </li>
                                </ul>
                                <div
                                  v-if="!canShowTradieField(item)"
                                  class="mobile-tradie__meta mobile-tradie__meta--lock d-flex align-items-center"
                                >
                                  <img
                                    src="@/assets/images/lock.png"
                                    alt="Locked"
                                    class="mobile-tradie__lock-icon mr-1"
                                  />
                                  {{ tradieLockMessage() }}
                                </div>
                              </div>
                              <div
                                v-if="canShowTradieField(item)"
                                class="mobile-tradie__select"
                                @click.stop
                              >
                                <b-button
                                  :style="{
                                    backgroundColor: isTradieSelected(item.id)
                                      ? '#28a745'
                                      : 'white',
                                  }"
                                  class="mobile-tradie__select-btn"
                                  @click="toggleTradieSelection(item.id)"
                                >
                                  <b-icon-check2
                                    v-if="isTradieSelected(item.id)"
                                  />
                                </b-button>
                              </div>
                              <div
                                v-if="canRemoveTradies"
                                class="mobile-tradie__remove"
                                @click.stop
                              >
                                <b-button
                                  variant="outline-light"
                                  class="mobile-tradie__remove-btn"
                                  @click="removingTradie(true, item.id)"
                                >
                                  <b-icon-x-circle-fill variant="danger" />
                                </b-button>
                              </div>
                            </div>
                            <div
                              v-if="!attentionTradies.length"
                              class="text-center py-4 text-muted"
                            >
                              There are no records to show.
                            </div>
                          </div>

                          <!-- Non Responding Tradies Table (desktop) -->
                          <div class="table-wrapper d-none d-md-block">
                            <b-table
                              id="attention-tradies-table"
                              :items="attentionTradiesPage"
                              :fields="attentionTradieFields"
                              show-empty
                              responsive
                              hover
                              :tbody-tr-class="getRowClass"
                              class="tradies-table"
                              @row-clicked="viewTradie"
                            >
                              <template #cell(project_approved)="{ item }">
                                <b-icon-check-circle-fill
                                  v-if="item.project_approved"
                                  v-b-tooltip.hover
                                  title="Project approved"
                                  class="text-success project-approved-tick"
                                  aria-label="Project approved"
                                />
                              </template>

                              <template #cell(full_name)="data">
                                <div class="cell-content-wrapper">
                                  <span
                                    :class="{
                                      'text-muted': isTradieTokenLocked(
                                        data.item,
                                      ),
                                    }"
                                  >
                                    {{
                                      data.item.full_name == ''
                                        ? '-----'
                                        : data.item.full_name
                                    }}
                                  </span>
                                  <div
                                    v-if="isTradieTokenLocked(data.item)"
                                    class="lock-overlay"
                                    :ref="`lock-over-lay-${data.item.id}`"
                                  >
                                    <img
                                      src="@/assets/images/lock.png"
                                      alt="Locked"
                                      class="lock-icon mr-2"
                                    />
                                    <span>{{ tradieLockMessage() }}</span>
                                  </div>
                                </div>
                              </template>

                              <template #cell(email)="data">
                                <span v-if="canShowTradieField(data.item)">{{
                                  data.item.email
                                }}</span>
                              </template>

                              <template #cell(trade_names)="data">
                                <span
                                  v-if="canShowTradieField(data.item)"
                                  v-b-tooltip.html
                                  :title="getTooltipText(data.item.trade_names)"
                                  variant="outline-success"
                                >
                                  {{ shortenString(data.item.trade_names, 30) }}
                                </span>
                              </template>

                              <template #cell(proof_of_id_files)="data">
                                <span
                                  v-if="canShowTradieField(data.item)"
                                  v-b-tooltip.html
                                  :title="
                                    getTooltipText(data.item.proof_of_id_files)
                                  "
                                  variant="outline-success"
                                >
                                  {{
                                    shortenString(data.item.proof_of_id_files)
                                  }}
                                </span>
                              </template>

                              <template #cell(need_attention)="{ item }">
                                <span v-if="canShowTradieField(item)">
                                  {{ item.need_attention ? 'Yes' : 'No' }}
                                </span>
                              </template>

                              <template
                                #cell(need_attention_reasons)="{ item }"
                              >
                                <span v-if="!canShowTradieField(item)"></span>
                                <span
                                  v-else-if="!needAttentionReasons(item).length"
                                  class="text-muted"
                                >
                                  –
                                </span>
                                <ul
                                  v-else
                                  class="attention-reasons text-danger"
                                >
                                  <li
                                    v-for="(reason, i) in needAttentionReasons(
                                      item,
                                    )"
                                    :key="i"
                                    class="attention-reason"
                                  >
                                    {{ reason }}
                                  </li>
                                </ul>
                              </template>

                              <template #cell(token_expiry)="{ item }">
                                <div v-if="canShowTradieField(item)">
                                  <span v-if="tokenExpiryDates[item.id]">
                                    <span
                                      :class="{
                                        'text-danger font-weight-bold':
                                          isExpiringSoon(
                                            tokenExpiryDates[item.id],
                                            30,
                                          ),
                                        'text-warning':
                                          isExpiringSoon(
                                            tokenExpiryDates[item.id],
                                            60,
                                          ) &&
                                          !isExpiringSoon(
                                            tokenExpiryDates[item.id],
                                            30,
                                          ),
                                      }"
                                    >
                                      {{
                                        formatExpiryDate(
                                          tokenExpiryDates[item.id],
                                        )
                                      }}
                                    </span>
                                  </span>
                                  <span
                                    v-else-if="
                                      tokenExpiryDates[item.id] === null
                                    "
                                    class="text-muted"
                                    style="font-size: 0.85rem"
                                    >-</span
                                  >
                                  <span
                                    v-else
                                    class="text-muted"
                                    style="font-size: 0.85rem"
                                    >Loading...</span
                                  >
                                </div>
                                <span v-else-if="isTradieTokenLocked(item)"
                                  >-</span
                                >
                              </template>

                              <template #cell(actions)="{ item }">
                                <b-button
                                  v-if="canRemoveTradies"
                                  variant="outline-light"
                                  style="height: 32px"
                                  class="px-2"
                                  @click="removingTradie(true, item.id)"
                                >
                                  <b-icon-x-circle-fill variant="danger" />
                                </b-button>
                              </template>
                            </b-table>
                          </div>

                          <b-pagination
                            v-if="attentionTradies.length > perPage3"
                            v-model="currentPage3"
                            :total-rows="attentionTradies.length"
                            :per-page="perPage3"
                            :limit="paginationLimit"
                            aria-controls="attention-tradies-table"
                            align="center"
                            class="tradies-pagination"
                          />

                          <p
                            v-if="attentionTradiesAll.length"
                            align="center"
                            class="tradie-count"
                          >
                            Showing {{ attentionTradiesPage.length }} out of
                            total of {{ attentionTradiesAll.length }} Needing
                            Attention
                          </p>
                        </b-col>
                      </b-row>
                    </template>
                    <!-- Purchase modal -->
                    <purchase-token-modal
                      v-if="canPurchaseTokens"
                      :showModal="isPurchasingToken"
                      @closeModal="purchasingToken(false)"
                      @purchase="onPurchaseTokens"
                    />

                    <!-- Assign modal -->
                    <assign-token-modal
                      v-if="isAssigningToken && canAssignTokens"
                      :showModal="isAssigningToken"
                      :tradieToAssign="tradieToAssign"
                      @closeModal="onAssignModalClose"
                      @assign="onAssignToken"
                      @purchaseTokens="onAssignModalPurchaseTokens"
                    />

                    <!-- Separator -->
                    <div class="section-separator"></div>

                    <!-- Expired Rquests -->
                    <b-row class="mobile-flush-row">
                      <!-- Label sits on its own full-width row above the
                           table instead of squeezing beside it, so the
                           table itself gets the full row width. -->
                      <b-col
                        cols="12"
                        class="font-weight-bold text-nowrap section-title"
                      >
                        Expired Requests:
                      </b-col>
                      <b-col cols="12" class="expired-requests-content-col">
                        <!-- Independent of the Tradies table's own search
                             box above — filters only this band. -->
                        <b-row>
                          <b-col
                            cols="12"
                            md="4"
                            class="mb-3 tradie-search band-search"
                          >
                            <b>Search (Name, Email, Role):</b>
                            <b-form-input
                              v-model="nSearch"
                              placeholder="Type to search"
                              debounce="500"
                            />
                          </b-col>
                        </b-row>

                        <!-- Mobile: stacked expired-request rows (hidden md+) -->
                        <div class="d-md-none mobile-tradie-list">
                          <div
                            v-for="item in nTradiesPage"
                            :key="item.id"
                            class="mobile-tradie-item"
                            :class="getRowClass(item)"
                            @click="viewTradie(item)"
                          >
                            <b-icon-check-circle-fill
                              v-if="item.project_approved"
                              class="text-success mobile-tradie__tick"
                              title="Project approved"
                            />
                            <div class="mobile-tradie__body">
                              <span
                                class="mobile-tradie__name"
                                :class="{
                                  'text-muted': isTradieTokenLocked(item),
                                }"
                              >
                                {{
                                  item.full_name === ''
                                    ? '-----'
                                    : item.full_name
                                }}
                              </span>
                              <div
                                v-if="canShowTradieField(item)"
                                class="mobile-tradie__meta"
                              >
                                <span v-if="item.email">{{ item.email }}</span>
                              </div>
                              <div
                                v-else
                                class="mobile-tradie__meta mobile-tradie__meta--lock d-flex align-items-center"
                              >
                                <img
                                  src="@/assets/images/lock.png"
                                  alt="Locked"
                                  class="mobile-tradie__lock-icon mr-1"
                                />
                                {{ tradieLockMessage() }}
                              </div>
                            </div>
                            <div
                              v-if="canRemoveTradies"
                              class="mobile-tradie__remove"
                              @click.stop
                            >
                              <b-button
                                variant="outline-light"
                                class="mobile-tradie__remove-btn"
                                @click="removingTradie(true, item.id)"
                              >
                                <b-icon-x-circle-fill variant="danger" />
                              </b-button>
                            </div>
                          </div>
                          <div
                            v-if="!nTradiesFiltered || !nTradiesFiltered.length"
                            class="text-center py-4 text-muted"
                          >
                            {{
                              nSearch
                                ? `No matches for "${nSearch}".`
                                : 'No expired requests.'
                            }}
                          </div>
                        </div>

                        <!-- Non Responding Tradies Table (desktop) -->
                        <div class="table-wrapper d-none d-md-block">
                          <b-table
                            v-if="nTradies"
                            :items="nTradiesPage"
                            :fields="nTradieFields"
                            show-empty
                            responsive
                            hover
                            :tbody-tr-class="getRowClass"
                            class="tradies-table"
                            @row-clicked="viewTradie"
                          >
                            <template #cell(project_approved)="{ item }">
                              <b-icon-check-circle-fill
                                v-if="item.project_approved"
                                v-b-tooltip.hover
                                title="Project approved"
                                class="text-success project-approved-tick"
                                aria-label="Project approved"
                              />
                            </template>

                            <template #cell(full_name)="data">
                              <div class="cell-content-wrapper">
                                <span
                                  :class="{
                                    'text-muted': isTradieTokenLocked(
                                      data.item,
                                    ),
                                  }"
                                >
                                  {{
                                    data.item.full_name == ''
                                      ? '-----'
                                      : data.item.full_name
                                  }}
                                </span>
                                <div
                                  v-if="isTradieTokenLocked(data.item)"
                                  class="lock-overlay"
                                  :ref="`lock-over-lay-${data.item.id}`"
                                >
                                  <img
                                    src="@/assets/images/lock.png"
                                    alt="Locked"
                                    class="lock-icon mr-2"
                                  />
                                  <span>{{ tradieLockMessage() }}</span>
                                </div>
                              </div>
                            </template>

                            <template #cell(email)="data">
                              <span v-if="canShowTradieField(data.item)">{{
                                data.item.email
                              }}</span>
                            </template>

                            <template #cell(actions)="{ item }">
                              <b-button
                                v-if="canRemoveTradies"
                                variant="outline-light"
                                style="height: 32px"
                                class="px-2"
                                @click="removingTradie(true, item.id)"
                              >
                                <b-icon-x-circle-fill variant="danger" />
                              </b-button>
                            </template>
                          </b-table>
                        </div>

                        <b-pagination
                          v-if="rows2 > perPage2"
                          v-model="currentPage2"
                          :total-rows="rows2"
                          :per-page="perPage2"
                          :current-page="currentPage2"
                          :limit="paginationLimit"
                          aria-controls="my-table-2"
                          align="center"
                          class="tradies-pagination"
                        />

                        <p v-if="nTradies" align="center" class="tradie-count">
                          Showing {{ nTradiesPage.length }} out of total of
                          {{ rows2 }} Expired Requests
                        </p>
                      </b-col>
                    </b-row>

                    <!-- Separator -->
                    <div class="section-separator"></div>

                    <!-- Expiring Files (in 30 days) -->
                    <b-row class="mobile-flush-row">
                      <!-- Label sits on its own full-width row above the
                           table instead of squeezing beside it, so the
                           table itself gets the full row width. -->
                      <b-col
                        cols="12"
                        class="font-weight-bold title-nowrap-mobile section-title"
                      >
                        Expiring Files (in 30 days):
                      </b-col>
                      <b-col cols="12">
                        <expiring-tradie-list
                          ref="expiringTradieList"
                          v-if="projectId"
                          :projectId="projectId"
                        />
                      </b-col>
                    </b-row>
                  </b-card-body>
                </b-card>

                <!-- Documents owned by the project itself (not a tradie).
                     Companies the project is shared with see the non-archived
                     ones read-only (the API tells the panel), same as on
                     SharedViewPage. -->
                <project-files-panel
                  v-if="projectId && canViewProjectFiles"
                  v-show="showFilesTab"
                  class="mt-3"
                  :projectId="projectId"
                />

                <!-- Jobs advertised for this project (live, expiring, expired). -->
                <project-jobs-panel
                  v-if="projectId && canViewProjectJobs && showJobsTab"
                  class="mt-3"
                  :projectId="projectId"
                />
              </b-container>
            </b-col>
          </b-row>
        </b-container>
      </b-container>
    </transition>
  </b-overlay>
</template>

<script>
import { canBuyTokens } from '@/utils/tokenAccess'
import { mapGetters, mapActions } from 'vuex'
import ProjectTradiesList from '@/components/lists/ProjectTradiesList'
import ExpiringTradieList from '@/components/lists/ExpiringTradieList'
import PurchaseTokenModal from '@/components/modals/PurchaseTokenModal'
import AssignTokenModal from '@/components/modals/AssignTokenModal'
import { applyTokenExpiryFromListTradies } from '@/utils/tradieListToken'
import {
  getTradieImportCsvFileValidationMessage,
  isTradieImportCsvFileMimeValidationMessage,
  parseTradieCsvImport422,
} from '@/utils/tradieCsvImportErrors'
import {
  TRADIE_IMPORT_ACCEPT,
  TRADIE_IMPORT_INVALID_TYPE_MESSAGE,
  validateTradieImportFile,
} from '@/utils/tradieImportFileValidation'
import TradieCsvImportErrorModal from '@/components/modals/TradieCsvImportErrorModal'
import ProjectFilesPanel from '@/components/projects/ProjectFilesPanel'
import ProjectJobsPanel from '@/components/projects/ProjectJobsPanel'
import ProjectPageTabs from '@/components/projects/ProjectPageTabs'
import projectPageTabs from '@/mixins/projectPageTabs'

export default {
  // Overview | Files tabs, kept in the URL as ?tab=files.
  mixins: [projectPageTabs],

  components: {
    ProjectFilesPanel,
    ProjectJobsPanel,
    ProjectPageTabs,
    ProjectTradiesList,
    ExpiringTradieList,
    PurchaseTokenModal,
    AssignTokenModal,
    TradieCsvImportErrorModal,
    ShareTradiesModal: () =>
      import(
        /* webpackChunkName: "share-tradies-modal" */
        /* webpackPrefetch: true */
        '@/components/modals/ShareTradiesModal.vue'
      ),
  },

  // Record the route the user came from so "Back to Projects" returns there
  // (the dashboard, a tradie profile, search results...) with its query
  // state intact, instead of always hard-pushing projects.list-page. Skip
  // this project's own sub-pages (its tradie view, or another project) so
  // bouncing in and out of them keeps the real outside origin.
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      if (
        from &&
        from.name &&
        !['projects.view-page', 'projects.view-tradie'].includes(from.name)
      ) {
        vm.originRoute = from.fullPath
      }
    })
  },

  beforeRouteUpdate(to, from, next) {
    if (
      from &&
      from.name &&
      !['projects.view-page', 'projects.view-tradie'].includes(from.name)
    ) {
      this.originRoute = from.fullPath
    }
    next()
  },

  data() {
    return {
      // Project
      projectId: parseInt(this.$route.params.id),
      // Where the user came from; set by the route guards above, null on a
      // direct link / hard reload.
      originRoute: null,
      cProject: null,

      search: '',
      // Tracks viewport width so the tradies table can shorten labels on phones.
      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1200,
      // Project's Tradies
      perPage: 10,
      currentPage: 1,

      // Non Responding Tradies (Expired Requests) — its own independent
      // search box, separate from the main Tradies table's `search`.
      perPage2: 5,
      currentPage2: 1,
      nSearch: '',

      // Needing Attention band — paginated client-side since the whole
      // list is already loaded (see fetchAttentionTradies/getProjectTradies).
      // Its own independent search box too.
      perPage3: 10,
      currentPage3: 1,
      attentionSearch: '',

      // Shared across every b-pagination on this page (and
      // ExpiringTradieList's own) so they all cap out at the same number of
      // visible page buttons — keeps their rendered width/size consistent
      // regardless of how many total pages any one table happens to have.
      paginationLimit: 5,

      // Templates
      selectedTemplates: [],
      isRemovingTemplate: false,
      templateId: null,
      templateName: null,

      // Tradies
      selectedTradieIds: [],
      isRemovingTradie: false,
      tradieId: null,
      tradieName: null,

      // Status Booleans
      isLoading: false,
      isDeletingProject: false,
      isEditingProject: false,
      isAddingTemplate: false,
      isAddingTradie: false,
      isImportingTradieCSV: false,
      showImportCsvErrorModal: false,
      importCsvErrorModalVariant: 'failure',
      importCsvErrorSummary: '',
      importCsvErrorEmails: [],
      importCsvErrorSkippedAlreadyInCompany: [],
      importCsvErrorSkippedBusinessAdmins: [],
      importCsvErrorImportedEmails: [],
      importCsvErrorInvitedEmails: [],
      isFetchingTemplateOptions: false,
      isShareSelectedTradies: false,

      // Tokens
      isAssigningToken: false,
      tradieToAssign: null,
      isPurchasingToken: false,
      tokenQuantity: null,
      // Tradie import file (CSV or .xlsx)
      tradieCsvFile: null,
      tradieImportFileClientHint: null,
      tradieImportAccept: TRADIE_IMPORT_ACCEPT,

      selectedTradies: [],
      selectAllTradiesBusy: false,
      tokenExpiryDates: {},

      // Full, search-independent tradie list backing the "Needing Attention"
      // band so it doesn't vanish when the main search box is cleared/changed.
      attentionTradiesSource: [],

      // Table Fields
      requiredFileFields: [
        { key: 'label', label: 'Template Name' },
        'description',
        'Actions',
      ],
    }
  },

  computed: {
    ...mapGetters('project', [
      'project',
      'tradies',
      'tradiesMeta',
      'nTradies',
      'nTradiesMeta',
      'templateOptions',
    ]),

    ...mapGetters('token', ['balance']),

    ...mapGetters('auth', ['user', 'hasPermission', 'isProjectScoped']),

    isBusinessAdmin() {
      return !!(this.user && this.user.user_type === 'business_admin')
    },

    /**
     * Free tier / still inside the 30-day trial — `is_free` on /auth/me, the
     * same flag the router and every token modal gate on. These companies
     * skip the token economy entirely (the API applies its free bypass), so
     * token-only UI like the Token Expiry column is hidden for them.
     */
    isFree() {
      return !!(this.user && this.user.is_free)
    },

    isSupervisorTradie() {
      return !!(
        this.user &&
        this.user.user_type === 'tradie' &&
        this.user.is_supervisor
      )
    },

    visibleTradies() {
      return this.tradies || []
    },

    /**
     * All tradies needing attention, independent of the search box. Derived
     * from a dedicated, search-independent fetch (`attentionTradiesSource`).
     * Drives whether the Needing Attention band is shown at all: the band
     * appears whenever the project has any tradie needing attention, so it
     * stays visible when the search box is cleared/changed. Falls back to the
     * loaded page while that fetch is in flight or if it failed.
     */
    attentionTradiesAll() {
      const source =
        this.attentionTradiesSource && this.attentionTradiesSource.length
          ? this.attentionTradiesSource
          : this.tradies || []
      return source.filter((x) => x && x.need_attention)
    },

    /**
     * Tradies needing attention, further narrowed by this band's own search
     * box (independent of the Tradies table's `search`) so the band's
     * contents track what's typed there without affecting any other
     * section. When the search filters everything out, this is empty and
     * the table/list render their "no records" empty state (the band
     * itself stays visible via `attentionTradiesAll`).
     */
    attentionTradies() {
      const term = (this.attentionSearch || '').trim().toLowerCase()
      if (!term) return this.attentionTradiesAll
      return this.attentionTradiesAll.filter((x) => {
        const haystack = [x.full_name, x.email, x.trade_names]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
        return haystack.includes(term)
      })
    },

    /**
     * One page's worth of `attentionTradies`, for the band's own pagination.
     * Everything is already loaded client-side (see fetchAttentionTradies),
     * so this just slices rather than issuing another request.
     */
    attentionTradiesPage() {
      const start = (this.currentPage3 - 1) * this.perPage3
      return this.attentionTradies.slice(start, start + this.perPage3)
    },

    /**
     * Tradies table columns. Progressively reveals columns as the viewport
     * grows so the table stays usable at 320px and expands into the full
     * desktop table by the `lg` breakpoint:
     *   - xs (<576):   tick, Name, Select
     *   - sm (>=576):  + Email, + Remove
     *   - md (>=768):  + Roles, + Token Expiry
     *   - lg (>=992):  + Proof of ID, + Needing Attention  (= full table)
     */
    tradieFields() {
      const short = this.windowWidth < 576
      return [
        {
          key: 'project_approved',
          label: '',
          thClass: 'project-approved-th',
          tdClass: 'project-approved-td',
        },
        {
          key: 'full_name',
          label: short ? 'Name' : 'Full Name',
          thClass: 'text-nowrap',
          tdClass: 'tradie-name-cell',
        },
        {
          key: 'email',
          label: 'Email',
          thClass: 'text-nowrap d-none d-sm-table-cell',
          tdClass: 'd-none d-sm-table-cell',
          formatter: (value) => {
            if (!value) return '-'
            return value.length > 30 ? value.substring(0, 30) + '...' : value
          },
        },
        {
          key: 'trade_names',
          label: 'Roles',
          thClass: 'text-nowrap d-none d-md-table-cell',
          tdClass: 'text-nowrap d-none d-md-table-cell',
        },
        {
          key: 'proof_of_id_files',
          label: 'Proof of ID',
          thClass: 'text-nowrap d-none d-lg-table-cell',
          tdClass: 'd-none d-lg-table-cell',
        },
        {
          key: 'need_attention',
          label: 'Needing Attention',
          thClass: 'text-nowrap d-none d-lg-table-cell',
          tdClass: 'd-none d-lg-table-cell',
        },
        // Free tier / trial companies skip the token economy entirely, so
        // there's no expiry to show — omit the column for them.
        ...(this.isFree
          ? []
          : [
              {
                key: 'token_expiry',
                label: 'Token Expiry',
                sortable: false,
                thClass: 'text-nowrap d-none d-md-table-cell',
                tdClass: 'd-none d-md-table-cell',
              },
            ]),
        {
          key: 'actions',
          label: short ? '' : 'Remove',
          thClass: 'text-nowrap d-none d-sm-table-cell',
          tdClass: 'd-none d-sm-table-cell',
        },
        {
          key: 'select',
          label: 'Select',
          thClass: 'text-center text-nowrap',
          tdClass: 'text-center',
        },
      ]
    },

    /**
     * Needing Attention table columns: the main tradies table minus Proof of
     * ID and the Select checkbox column (bulk actions aren't offered from that
     * band), plus a "Reason" column — the why lives only in this band, not the
     * main tradies table.
     */
    attentionTradieFields() {
      const fields = this.tradieFields.filter(
        (field) => !['proof_of_id_files', 'select'].includes(field.key),
      )
      const reasonField = {
        key: 'need_attention_reasons',
        label: 'Reason',
        // Shown from md (768) — the band's whole point — not just lg. The
        // 768–1400 compact media query keeps it fitting without a scrollbar.
        thClass: 'text-nowrap d-none d-md-table-cell',
        tdClass: 'd-none d-md-table-cell attention-reason-cell',
      }
      const at = fields.findIndex((f) => f.key === 'need_attention')
      if (at === -1) fields.push(reasonField)
      else fields.splice(at + 1, 0, reasonField)
      return fields
    },

    /**
     * Expired Requests table columns. Mirrors the tradies table's
     * responsive behaviour: Name (+ Remove) stay visible down to 320px,
     * Email is progressively revealed from `sm` up, and the table stays
     * compact + horizontally scrollable via the shared `.tradies-table`
     * styling.
     */
    nTradieFields() {
      const short = this.windowWidth < 576
      return [
        {
          key: 'full_name',
          label: short ? 'Name' : 'Full Name',
          thClass: 'text-nowrap',
        },
        {
          key: 'email',
          label: 'Email',
          thClass: 'text-nowrap d-none d-sm-table-cell',
          tdClass: 'd-none d-sm-table-cell',
          formatter: (value) => {
            if (!value) return '-'
            return value.length > 30 ? value.substring(0, 30) + '...' : value
          },
        },
        {
          key: 'actions',
          label: short ? '' : 'Remove',
          // With only 3 columns here (vs. the main/Needing Attention
          // tables' 6-7), auto table-layout gave this column a lot more
          // slack width than its content needs — right-aligning the content
          // wasn't enough on its own since the column itself still spanned
          // most of the row. width: 1% is the standard "shrink this column
          // to its content" table trick: the browser can't actually give it
          // 1%, so it falls back to the column's min content width and
          // hands the reclaimed slack to `email` instead, landing the
          // button right at the table's edge.
          thClass: 'text-nowrap text-right',
          tdClass: 'text-right',
          thStyle: { width: '1%' },
        },
      ]
    },

    canDeleteProject() {
      if (!this.isBusinessAdmin) return false
      return this.hasPermission('projects.delete')
    },

    canEditProject() {
      if (!this.isBusinessAdmin) return false
      return this.hasPermission('projects.edit')
    },

    canExportMatrix() {
      if (!this.isBusinessAdmin) return false
      return this.hasPermission('projects.export')
    },

    canAddTradies() {
      if (!this.isBusinessAdmin) return false
      return this.hasPermission('projects.tradies.add')
    },

    canRemoveTradies() {
      if (!this.isBusinessAdmin) return false
      return this.hasPermission('projects.tradies.remove')
    },

    canShareTradies() {
      if (!this.isBusinessAdmin) return false
      return this.hasPermission('projects.tradies.share')
    },

    canViewProjectFiles() {
      return this.hasPermission('projects.files.view')
    },

    // Company job postings are business-admin only, and never for
    // project-scoped roles (same rule as the /jobs routes).
    canViewProjectJobs() {
      return (
        !!this.user &&
        this.user.user_type === 'business_admin' &&
        !this.isProjectScoped &&
        this.hasPermission('jobs.view')
      )
    },

    canDownloadTradieFiles() {
      if (!this.isBusinessAdmin) return true
      return this.hasPermission('projects.tradie.files.download')
    },

    canPurchaseTokens() {
      return canBuyTokens(this.user, this.hasPermission)
    },

    canAssignTokens() {
      return (
        (this.user && this.user.is_primary) ||
        this.hasPermission('settings.billing.assign')
      )
    },

    breadcrumbs() {
      return [
        {
          text: 'Projects',
          to: '/projects',
        },
        {
          text: this.project ? this.project.name : '',
        },
      ]
    },

    rows() {
      return this.tradiesMeta ? this.tradiesMeta.total : 0
    },

    /**
     * Expired Requests (nTradies), filtered by this band's own search box.
     * The non-responding-tradies endpoint doesn't honour a `search` query
     * param (unlike the Tradies table's own endpoint), so — same approach
     * as the Needing Attention band — the whole list is fetched once
     * (mounted, with a large limit) and search/pagination are both done
     * client-side instead of round-tripping to the API.
     */
    nTradiesFiltered() {
      const term = (this.nSearch || '').trim().toLowerCase()
      const all = this.nTradies || []
      if (!term) return all
      return all.filter((x) => {
        const haystack = [x.full_name, x.email]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
        return haystack.includes(term)
      })
    },

    /** One page's worth of `nTradiesFiltered`. */
    nTradiesPage() {
      const start = (this.currentPage2 - 1) * this.perPage2
      return this.nTradiesFiltered.slice(start, start + this.perPage2)
    },

    // Non Responding Tradies
    rows2() {
      return this.nTradiesFiltered.length
    },
  },

  watch: {
    currentPage: async function (newCurrentPage) {
      this.tokenExpiryDates = {}
      await this.getTradiesByProjectId({
        projectId: this.projectId || parseInt(this.$route.params.id),
        search: this.search,
        limit: this.perPage,
        page: newCurrentPage,
      })
      applyTokenExpiryFromListTradies(this, this.tradies)
      // Re-hydrate the Needing Attention rows after the reset above.
      applyTokenExpiryFromListTradies(this, this.attentionTradiesSource)
    },

    // Expired Requests' own independent search box — separate from the
    // Tradies table's `search` and the Needing Attention band's
    // `attentionSearch` below. Filtering/pagination are both done
    // client-side (nTradiesFiltered/nTradiesPage computed) since the whole
    // list is already loaded, so this just resets to page 1 rather than
    // issuing another request.
    nSearch() {
      this.currentPage2 = 1
    },

    search: async function (newSearch) {
      this.search = newSearch
      if (this.currentPage !== 1) {
        this.currentPage = 1
      } else {
        await this.fetchTradies()
      }
    },

    // Needing Attention band's own independent search box — filtering is
    // done client-side (attentionTradies computed), so this just needs to
    // reset pagination rather than issue another request.
    attentionSearch() {
      this.currentPage3 = 1
    },
  },

  methods: {
    ...mapActions('project', {
      getProject: 'getProject',
      deleteProject: 'deleteProject',
      updateProject: 'updateProject',
      getTemplateOptions: 'getTemplateOptions',
      addTemplate: 'addTemplate',
      removeTemplate: 'removeTemplate',
      addTradies: 'addTradies',
      removeTradie: 'removeTradie',
      importTradieCSV: 'importTradieCSV',
      getTradiesByProjectId: 'getTradiesByProjectId',
      getProjectTradies: 'getProjectTradies',
      getNonRespondingTradies: 'getNonRespondingTradies',
    }),

    ...mapActions('token', {
      purchase: 'purchase',
      assign: 'assign',
      getBalance: 'getBalance',
    }),

    onResize() {
      this.windowWidth = window.innerWidth
    },

    checkIfAccepted(item) {
      return item.accepted
    },

    selectTradie(status, tradieId = null) {
      if (!this.selectedTradies.includes(tradieId)) {
        this.selectedTradies.push(tradieId)
      }
    },

    deselectTradie(status, tradieId = null) {
      this.selectedTradies = this.selectedTradies.filter(
        (id) => id !== tradieId,
      )
    },

    toggleTradieSelection(tradieId) {
      if (this.isTradieSelected(tradieId)) {
        this.deselectTradie(true, tradieId)
      } else {
        this.selectTradie(true, tradieId)
      }
    },

    tradiesListFromProjectDetailsResponse(apiBody) {
      const inner = apiBody && apiBody.data
      const wrap = inner && inner.tradies
      if (!wrap || !Array.isArray(wrap.data)) return []
      return wrap.data
    },

    async fetchAllAccessibleTradieIdsForProjectTable() {
      const meta = this.tradiesMeta || {}
      const total = meta.total != null ? meta.total : this.rows
      if (!total) return []

      const perPage = this.perPage
      const lastPage =
        meta.last_page != null
          ? meta.last_page
          : Math.max(1, Math.ceil(total / perPage))

      const projectId = this.projectId || parseInt(this.$route.params.id, 10)
      const paramsBase = { limit: perPage }
      if (this.search) paramsBase.search = this.search

      const tasks = []
      for (let p = 1; p <= lastPage; p++) {
        tasks.push(
          this.$api.get(`projects/${projectId}`, {
            params: { ...paramsBase, page: p },
          }),
        )
      }
      const responses = await Promise.all(tasks)
      const ids = []
      responses.forEach((axiosRes) => {
        const list = this.tradiesListFromProjectDetailsResponse(axiosRes.data)
        list.forEach((tradie) => {
          if (tradie && tradie.can_access && tradie.id != null) {
            ids.push(tradie.id)
          }
        })
      })
      return [...new Set(ids)]
    },

    async toggleSelectAllTradies() {
      if (this.selectAllTradiesBusy || this.isLoading) return

      this.selectAllTradiesBusy = true
      try {
        const allIds = await this.fetchAllAccessibleTradieIdsForProjectTable()
        if (!allIds.length) return

        const allInSelection = allIds.every((id) =>
          this.selectedTradies.includes(id),
        )

        if (allInSelection) {
          this.selectedTradies = this.selectedTradies.filter(
            (id) => !allIds.includes(id),
          )
        } else {
          allIds.forEach((id) => {
            if (!this.selectedTradies.includes(id)) {
              this.selectedTradies.push(id)
            }
          })
        }
      } catch (e) {
        console.error(e)
        this.showToast(
          'Error',
          'Could not load all tradies for this list. Try again.',
          'danger',
        )
      } finally {
        this.selectAllTradiesBusy = false
      }
    },
    async fetchTradies() {
      this.isLoading = true
      this.tokenExpiryDates = {}

      await this.getTradiesByProjectId({
        projectId: this.projectId,
        limit: this.perPage,
        page: this.currentPage,
        search: this.search,
      })
        .catch((err) => console.log(err))
        .finally(() => {
          this.isLoading = false
        })

      applyTokenExpiryFromListTradies(this, this.tradies)
      // Re-hydrate the Needing Attention rows after the reset above.
      applyTokenExpiryFromListTradies(this, this.attentionTradiesSource)
    },

    async downloadFile() {
      if (!this.canDownloadTradieFiles) return

      // check if any users are selected
      if (this.selectedTradies.length === 0) {
        this.showToast(
          'Warning',
          'Please select at least one tradie to download',
          'warning',
        )
        return
      }
      // Snapshot now, before any await — `project` is a shared store getter,
      // and navigating away to a different project mid-download re-dispatches
      // getProject(), which clears it immediately (see store/modules/project.js
      // getProject()). Reading this.project.name after the awaits below would
      // then see the OTHER project (or null), mislabelling this download as
      // "Project_Unknown_...".
      const projectNameSnapshot =
        this.project && this.project.name ? this.project.name : 'Unknown'
      try {
        this.showToast('Success', 'Downloading tradie files!')
        // this.isLoading = true

        // get file ids for selected tradies
        const tradieIdsString = this.selectedTradies.join(',')
        // console.log('Step 1: Getting file IDs for tradies:', tradieIdsString)

        const { data: fileIdsResponse } = await this.$api.get(
          '/tradie-file-ids',
          { params: { tradie_ids: tradieIdsString } },
        )

        // console.log('Step 2: File IDs response:', fileIdsResponse)
        const tradiesPayload = this.selectedTradies.map((id) => ({ id }))
        // if empty say tradies have no files to download
        const fileIds = fileIdsResponse.data
        // console.log('Step 3: Extracted file IDs:', fileIds)
        // if (fileIds.length === 0) {
        //   this.showToast(
        //     'Info',
        //     'Tradie has no files, downloading personal information ',
        //     'info',
        //   )
        // }

        // call download bulk files with those ids
        // console.log(
        //   'Step 4: Calling download-bulk-files with file IDs:',
        //   fileIds,
        // )

        const { data: zip } = await this.$api.get('download-bulk-files', {
          params: { files: fileIds, tradies: JSON.stringify(tradiesPayload) },
          responseType: 'blob',
        })

        // console.log('Step 5: Received ZIP blob, size:', zip.size)

        // create download link
        const filename = `Project_${projectNameSnapshot}_Tradies_Files.zip`
        const url = URL.createObjectURL(
          new Blob([zip], { type: 'application/zip' }),
        )
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)

        this.showToast(
          'Success',
          `Downloaded ${fileIds.length} files from ${this.selectedTradies.length} tradies!`,
        )
      } catch (error) {
        this.showToast('Error', 'Failed to download files', 'danger')
        console.error('Download error:', error)
      } finally {
        this.isLoading = false
      }
    },

    async downloadTicketsCsv() {
      // Snapshot before any await — see the identical comment in
      // downloadFile() above for why this can't be read after the request.
      const projectNameSnapshot =
        this.project && this.project.name ? this.project.name : 'Unknown'
      try {
        this.isLoading = true

        const params = {}
        // If any tradies are selected, export only those; otherwise, export all
        if (this.selectedTradies && this.selectedTradies.length > 0) {
          params.tradie_ids = this.selectedTradies.join(',')
        }

        const response = await this.$api.get(
          `/projects/${this.projectId}/tradie-tickets-csv`,
          {
            params,
            responseType: 'blob',
          },
        )

        // Zero exportable rows (e.g. every file for these tradies is still
        // pending malware scan) comes back as a JSON message instead of a
        // near-empty CSV, so there's a clear "nothing to export" signal
        // instead of a silent, misleadingly-successful-looking download.
        const contentType =
          (response.headers && response.headers['content-type']) || ''
        if (contentType.includes('application/json')) {
          const text = await response.data.text()
          const { message } = JSON.parse(text)
          this.showToast(
            'Nothing to export',
            message ||
              'No files available to export for the selected tradie(s).',
            'warning',
          )
          return
        }

        const suffix =
          this.selectedTradies && this.selectedTradies.length > 0
            ? '_Selected'
            : '_All'
        const filename = `Project_${projectNameSnapshot}_Tradie_Tickets${suffix}.csv`

        const blob = new Blob([response.data], {
          type: 'text/csv;charset=utf-8;',
        })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)

        this.showToast(
          'Success',
          `CSV download started for ${
            (this.selectedTradies && this.selectedTradies.length) || this.rows
          } tradies.`,
        )
      } catch (err) {
        this.showToast('Error', 'Failed to download tickets CSV', 'danger')
        console.error('Tickets CSV download error:', err)
      } finally {
        this.isLoading = false
      }
    },

    isTradieSelected(tradieId) {
      return this.selectedTradies.includes(tradieId)
    },

    shareSelectedTradies(status) {
      this.isShareSelectedTradies = status
    },

    async fetchProject() {
      this.isLoading = true
      this.tokenExpiryDates = {}

      await this.getProject({
        id: this.projectId,
        limit: this.perPage,
        page: this.currentPage,
      })
        .catch((err) => this.handleError(err))
        .finally(() => {
          this.isLoading = false
          this.cProject = JSON.parse(JSON.stringify(this.project))
        })

      applyTokenExpiryFromListTradies(this, this.tradies)

      // Refresh the search-independent list that powers the Needing Attention
      // band, so it reflects the latest data after any project reload.
      this.fetchAttentionTradies()
    },

    /**
     * Loads the project's tradies without a search filter and caches them so
     * the "Needing Attention" band can stay visible independent of the main
     * tradies table's search/pagination state.
     */
    async fetchAttentionTradies() {
      const projectId = this.projectId || parseInt(this.$route.params.id, 10)
      if (!projectId) return
      try {
        const list = await this.getProjectTradies(projectId)
        this.attentionTradiesSource = Array.isArray(list) ? list : []
        // The band's rows read tokenExpiryDates too; without this, tradies not
        // on the main table's current page stay stuck on "Loading...".
        applyTokenExpiryFromListTradies(this, this.attentionTradiesSource)
      } catch (err) {
        console.log(err)
      }
    },

    gotoProjects() {
      if (this.originRoute) {
        this.$router.push(this.originRoute).catch(() => {})
      } else {
        this.$router.push({ name: 'projects.list-page' })
      }
    },

    deletingProject(status) {
      this.isDeletingProject = status
    },

    async onDeleteProject() {
      await this.deleteProject(this.projectId)
        .catch((err) => this.handleError(err))
        .finally(() => {
          this.$router.push({
            name: 'projects.list-page',
          })
        })
    },

    editingProject(status) {
      this.isEditingProject = status
    },
    assigningToken(item) {
      this.isAssigningToken = true
      this.tradieToAssign = item
    },

    async onAssignToken() {
      if (!this.tradieToAssign) {
        return
      }

      // get company id
      const companyId = this.user.company_id

      if (!companyId) {
        this.showToast('Error', 'Company ID not found', 'danger')
        return
      }
      // isLoading true
      this.isLoading = true
      try {
        // try and await this
        await this.assign(this.tradieToAssign.id, companyId)
        // show toast
        this.showToast('Success', 'Assigning token to tradie')
        // set assign to false
        this.isAssigningToken = false
        // await fetch trdies
        await this.fetchProject()
        // refresh expiring tokens
        if (this.$refs.expiringTradieList) {
          await this.$refs.expiringTradieList.fetchExpiringTradieFiles(
            this.$refs.expiringTradieList.currentPage,
            this.$refs.expiringTradieList.perPage,
          )
        }
        // awwait $store.dispatch token balance
        await this.$store.dispatch('token/getBalance')
      } catch (err) {
        this.handleError(err)
      } finally {
        this.isLoading = false
      }
    },

    purchasingToken(status) {
      if (!status) {
        this.tokenQuantity = 1
      }
      this.isPurchasingToken = status
    },

    async onPurchaseTokens(quantity) {
      if (!quantity || quantity < 1) {
        return
      }
      // const quantity = parseInt(quantity, 10)
      console.log('quantity', quantity)

      await this.purchase(quantity).catch((err) => this.handleError(err))
      this.purchasingToken(false)
    },

    onAssignModalClose() {
      this.isAssigningToken = false
      this.tradieToAssign = null
    },

    onAssignModalPurchaseTokens() {
      this.isAssigningToken = false
      if (this.canPurchaseTokens) {
        this.purchasingToken(true)
      }
    },

    async onUpdateProject() {
      this.isLoading = true

      await this.updateProject(this.cProject)
        .then((res) => {
          this.$bvToast.toast('Project updated successfully!', {
            title: 'Success',
            autoHideDelay: 3000,
            variant: 'success',
          })
        })
        .catch((err) => this.handleError(err))
        .finally(() => {
          this.isLoading = false
          this.editingProject(false)
        })
    },

    missingFilesFormat(missingFiles) {
      var fileNames = ''

      missingFiles.forEach(function (file, index) {
        fileNames += file.label + (index < missingFiles.length - 1 ? ', ' : '')
      })

      return fileNames
    },

    async addingTemplate(status) {
      this.isAddingTemplate = status

      if (status) {
        this.selectedTemplates = []
        await this.fetchTemplateOptions()
      }
    },

    importingTradieCSV(status) {
      this.tradieImportFileClientHint = null
      this.isImportingTradieCSV = status
    },

    onTradieImportFileChange() {
      this.tradieImportFileClientHint = null
      this.$nextTick(() => {
        const file = this.tradieCsvFile
        if (!file) return
        const fileCheck = validateTradieImportFile(file)
        if (!fileCheck.ok) {
          this.tradieCsvFile = null
          if (fileCheck.message === TRADIE_IMPORT_INVALID_TYPE_MESSAGE) {
            this.tradieImportFileClientHint = fileCheck.message
            this.showToast('Invalid file', fileCheck.message, 'danger')
          } else {
            this.showToast('Import', fileCheck.message, 'danger')
          }
        }
      })
    },

    async onImportTradieCSV() {
      const fileCheck = validateTradieImportFile(this.tradieCsvFile)
      if (!fileCheck.ok) {
        if (fileCheck.message === TRADIE_IMPORT_INVALID_TYPE_MESSAGE) {
          this.tradieImportFileClientHint = fileCheck.message
        }
        const title =
          fileCheck.message === TRADIE_IMPORT_INVALID_TYPE_MESSAGE
            ? 'Invalid file'
            : 'Import'
        this.showToast(title, fileCheck.message, 'danger')
        return
      }

      this.isLoading = true
      try {
        await this.importTradieCSV({
          projectId: this.project.id,
          tradieCsvFile: this.tradieCsvFile,
        })
        this.fetchProject()
        this.showToast('Success', 'Tradies Added!')
        this.importingTradieCSV(false)
      } catch (err) {
        const fileValidationMsg = getTradieImportCsvFileValidationMessage(err)
        const mimeOnly =
          fileValidationMsg &&
          isTradieImportCsvFileMimeValidationMessage(fileValidationMsg)

        if (mimeOnly) {
          this.showToast('Import', fileValidationMsg, 'danger')
          return
        }

        const parsed = parseTradieCsvImport422(err)
        if (parsed) {
          const genericSummary =
            !parsed.summary ||
            /^the given data was invalid\.?$/i.test(parsed.summary.trim())
          const summary =
            fileValidationMsg && !mimeOnly
              ? fileValidationMsg
              : genericSummary && fileValidationMsg
              ? fileValidationMsg
              : parsed.summary

          this.importCsvErrorModalVariant = 'failure'
          this.importCsvErrorSummary = summary
          this.importCsvErrorEmails = parsed.emails
          this.importCsvErrorImportedEmails = parsed.importedEmails || []
          this.importCsvErrorInvitedEmails = parsed.invitedEmails || []
          this.importCsvErrorSkippedAlreadyInCompany =
            parsed.skippedAlreadyInCompany
          this.importCsvErrorSkippedBusinessAdmins =
            parsed.skippedBusinessAdmins
          this.showImportCsvErrorModal = true
        } else {
          this.handleError(err)
        }
      } finally {
        this.isLoading = false
      }
    },

    async onAddTemplate(event) {
      event.preventDefault()

      await this.addTemplate({
        id: this.project.id,
        templateIds: this.selectedTemplates,
      })
        .then((res) => this.showToast('Success', 'Template Added!'))
        .catch((err) => this.handleError(err))
        .finally(() => this.addingTemplate(false))
    },

    fetchingTemplateOptions(status) {
      this.isFetchingTemplateOptions = status
    },

    async fetchTemplateOptions() {
      this.fetchingTemplateOptions(true)

      await this.getTemplateOptions()
        .catch((err) => this.handleError(err))
        .finally(() => this.fetchingTemplateOptions(false))
    },

    removingTemplate(status, templateId = null) {
      this.isRemovingTemplate = status

      if (status) {
        this.templateId = templateId
        this.templateName = this.project.required_file_templates.find(
          (x) => x.id === templateId,
        ).label
      }
    },

    async onRemoveTemplate() {
      await this.removeTemplate({
        id: this.project.id,
        templateId: this.templateId,
      })
        .then((res) => this.showToast('Success', 'Template Removed!'))
        .catch((err) => this.handleError(err))
        .finally(() => this.removingTemplate(false))
    },

    addingTradie(status) {
      this.isAddingTradie = status

      if (status) {
        this.tradieIds = []
      }
    },

    async viewTradie(item, i) {
      // Gate on can_access, never on the presence of a token object. A company
      // with unrestricted access (free tier, or inside its first 30 days) is
      // sent `token: null` by design — TradieCompanyListHydrator returns early
      // for it — while can_access stays true. Checking the token therefore
      // refused rows this very page renders as unlocked, since
      // isTradieTokenLocked() below keys off can_access: the row looked
      // clickable, was clicked, and did nothing at all.
      // SharedViewPage.viewTradie() has always used can_access; this matches.
      if (!item || !item.can_access) {
        // A BA who can assign tokens gets the modal that actually fixes it.
        // Everyone else gets told why, instead of a click that dies silently.
        if (!this.isSupervisorTradie && this.canAssignTokens) {
          this.assigningToken(item)
          return
        }
        this.showToast('Locked', this.tradieLockMessage(), 'warning')
        return
      }
      this.$router.push({
        name: 'projects.view-tradie',
        params: {
          id: this.projectId,
          tradieId: item.id,
        },
      })
    },

    getRowClass(item) {
      if (!item) return ''
      const baseClass = 'cursor-pointer'
      if (this.isTradieTokenLocked(item)) {
        return `${baseClass} row-locked`
      }
      return baseClass
    },

    /** True when a tradie row is locked because no token is assigned. */
    isTradieTokenLocked(item) {
      return !!(item && !item.can_access)
    },

    canShowTradieField(item) {
      return !!(item && item.can_access)
    },

    // API-supplied list of why this tradie needs attention on the project
    // (missing trade docs, expired/expiring docs, missing/unapproved White
    // Card or photo ID). Empty when they're clear.
    needAttentionReasons(item) {
      return Array.isArray(item && item.need_attention_reasons)
        ? item.need_attention_reasons
        : []
    },

    tradieLockMessage() {
      if (this.isSupervisorTradie) {
        return 'Locked - Contact your business admin to unlock'
      }
      return 'Locked - Assign token to view'
    },

    positionLockOverlays() {
      if (!this.$el) return
      this.$el.querySelectorAll('.lock-overlay').forEach((overlay) => {
        const tr = overlay.closest('tr')
        const tableEl = overlay.closest('.tradies-table')
        if (!tr || !tableEl) return

        overlay.style.left = '0px'
        overlay.style.top = '0px'
        overlay.style.width = 'max-content'
        overlay.style.maxWidth = 'none'
        overlay.style.transform = 'none'
        const originRect = overlay.getBoundingClientRect()
        const trRect = tr.getBoundingClientRect()
        const tableRect = tableEl.getBoundingClientRect()

        const tableCenterX = tableRect.left + tableRect.width / 2
        overlay.style.left = `${
          tableCenterX - originRect.width / 2 - originRect.left
        }px`
        overlay.style.top = `${
          trRect.top -
          originRect.top +
          trRect.height / 2 -
          originRect.height / 2
        }px`
      })
    },

    formatExpiryDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleDateString('en-AU', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    },

    isExpiringSoon(expiryDate, days) {
      if (!expiryDate) return false
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const expiry = new Date(expiryDate)
      expiry.setHours(0, 0, 0, 0)
      const diffDays = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24))
      return diffDays >= 0 && diffDays <= days
    },

    async onAddTradie() {
      await this.addTradies({
        id: this.project.id,
        tradieIds: this.selectedTradieIds,
      })
        .then((res) => this.showToast('Success', 'Tradies Added'))
        .catch((err) => this.handleError(err))
        .finally(() => {
          this.addingTradie(false)
          this.fetchProject()
        })
    },

    removingTradie(status, tradieId = null) {
      this.isRemovingTradie = status

      if (status) {
        this.tradieId = tradieId
        this.tradieName = this.tradies.find((x) => x.id === tradieId).full_name
        if (this.tradieName === '') this.tradieName = '[No Name]'
      }
    },

    async onRemoveTradie() {
      this.isLoading = true

      await this.removeTradie({
        id: this.project.id,
        tradieId: this.tradieId,
      })
        .then((res) => {
          this.showToast('Success', 'Tradie Removed!')
          this.removingTradie(false)
          const remainingTotal = Math.max(this.rows - 1, 0)
          const lastPage = Math.max(Math.ceil(remainingTotal / this.perPage), 1)
          if (this.currentPage > lastPage) {
            this.currentPage = lastPage
          }
          this.fetchProject()
        })
        .catch((err) => this.handleError(err))
        .finally(() => (this.isLoading = false))
    },

    onTradieSelected(tradieIds) {
      this.selectedTradieIds = tradieIds
    },

    shortenString(arrayString, limit = 12) {
      const string = Array.from(arrayString).join(', ')
      return string.length > limit ? string.substring(0, limit) + '...' : string
    },

    getTooltipText(arrayString) {
      return Array.from(arrayString).join('<br/>')
    },

    showToast(title, message, variant = 'success') {
      this.$bvToast.toast(message, {
        title: title,
        autoHideDelay: 3000,
        variant: variant,
      })
    },

    handleError(err) {
      const msg =
        (err && err.data && err.data.message) ||
        (err && err.message) ||
        'Something went wrong. Please try again.'
      this.showToast('Oops.. Something went wrong!', msg, 'danger')
    },

    rowClass(item) {
      return item && item.need_attention
        ? 'cursor-pointer bg-alert'
        : 'cursor-pointer'
    },
  },

  async mounted() {
    this.fetchProject()

    if (!this.isProjectScoped) {
      this.getBalance()
    }
    // Get Non Responding Tradies (Expired File Requests) — fetched once in
    // full (see nTradiesFiltered/nTradiesPage) since the endpoint doesn't
    // support server-side search, matching the Needing Attention band's
    // own client-side filter/paginate approach.
    await this.getNonRespondingTradies({
      projectId: this.projectId,
      limit: 100,
      page: 1,
    }).catch((err) => this.handleError(err))

    this.$nextTick(() =>
      requestAnimationFrame(() => this.positionLockOverlays()),
    )
    window.addEventListener('resize', this.positionLockOverlays)
    window.addEventListener('resize', this.onResize)
  },

  updated() {
    this.$nextTick(() => this.positionLockOverlays())
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.positionLockOverlays)
    window.removeEventListener('resize', this.onResize)
  },
}
</script>

<style lang="scss" scoped>
.table-wrapper {
  position: relative;
  overflow: visible;
  max-width: 100%;
}

// "Reason" column (Needing Attention band): a bulleted list of why the tradie
// needs attention. Cap the column so it doesn't crowd out Roles / Token Expiry.
/deep/ .attention-reason-cell {
  max-width: 260px;
  white-space: normal;
}
.attention-reasons {
  margin: 0;
  padding-left: 1.1em;
  list-style: disc outside;
}
.attention-reason {
  font-size: 0.8rem;
  line-height: 1.3;
  white-space: normal;
}

/deep/ .tradies-table {
  overflow: visible !important;
}

/* Keep every tradies table contained within its column at ALL widths: it
   scrolls horizontally inside its own `.table-responsive` box rather than
   pushing past the card. Declared after the `overflow: visible` rule (and
   with !important) so it still wins if b-table merges the `tradies-table`
   class onto the responsive wrapper itself. */
/deep/ .table-responsive {
  overflow-x: auto !important;
  max-width: 100%;
}

/deep/ .tradies-table tbody tr.row-locked td {
  overflow: visible !important;
}

/deep/ .tradies-table .row-locked .lock-overlay,
/deep/ .tradies-table .row-locked .lock-overlay span {
  color: #999 !important;
}

/deep/ .tradies-table .row-locked .lock-overlay {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  pointer-events: none;
  font-size: 1.1rem;
  font-weight: 600;
  white-space: nowrap;
  width: max-content;
  max-width: none;
  opacity: 1 !important;
}

/deep/ .tradies-table .lock-icon {
  width: 32px;
  height: 32px;
  object-fit: contain;
  margin-right: 8px;
}

/* Table column titles larger than the row text below them. em (not rem) so
   this scales proportionally with .tradies-table's own font-size —
   including the 0.78rem it drops to on phones — instead of needing a
   separate desktop-only override. */
/deep/ .tradies-table th {
  font-size: 1.2em;
}

/* Section titles ("Tradies:", "Needing Attention:", "Expired Requests:",
   "Expiring Files (in 30 days):") — larger than the regular body text. */
.section-title {
  font-size: 1.2rem;
}

/* "Expiring Files (in 30 days):" is forced to one line on phones
   (.title-nowrap-mobile) — at the full 1.2rem it's wide enough to clip on a
   320px screen. Scale all four titles back a bit below sm so the longest
   one still fits. */
@media (max-width: 575.98px) {
  .section-title {
    font-size: 1.05rem;
  }
}

/deep/ .b-form-btn-label-control.form-control {
  height: 50px;
}

/deep/ label.form-control {
  padding-top: 10px;
}

/deep/ .cursor-pointer {
  cursor: pointer;
}

/deep/ .cursor-pointer td {
  vertical-align: middle;
}

/deep/ .excluded .custom-file-label {
  height: 69%;
}

.b-icon {
  transition: transform 0.5s ease;
}

.bg-light-darker {
  background-color: #e4e4e4 !important;
}

.btn-light-darker {
  background-color: #e4e4e4 !important;
  border-color: #e4e4e4 !important;
  color: #495057 !important;
}

.btn-light-darker:hover {
  background-color: #d2d5d5 !important;
  border-color: #d2d5d5 !important;
  color: #495057 !important;
}

.btn-light-darker:focus {
  box-shadow: 0 0 0 0.2rem rgba(240, 240, 240, 0.5) !important;
}

.no-outline:focus {
  box-shadow: none !important;
}

.progress-container {
  width: 100%;
  height: 8px;
  border-radius: 4px;
  background: #e9eef5;
}

.progress-bar {
  height: 100%;
  border-radius: 4px;
  background: #006cff;
  transition: width 0.2s ease;
}

.progress-bar--primary {
  background: #006cff;
}

.progress-bar--danger {
  background: #e52c00;
}

/deep/ .cursor-pointer {
  cursor: pointer;
}

.text-noproject {
  color: lightgray;
}

/deep/ .excluded .custom-file-label {
  height: 69%;
}

.hover-bg-darken:hover {
  background-color: #f0f0f0;
  transition: background-color 0.2s;
}

.b-icon {
  transition: transform 0.5s ease;
}

.bg-light-darker {
  background-color: #e4e4e4 !important;
}

.btn-light-darker {
  background-color: #e4e4e4 !important;
  border-color: #e4e4e4 !important;
  color: #495057 !important;
}

.btn-light-darker:hover {
  background-color: #d2d5d5 !important;
  border-color: #d2d5d5 !important;
  color: #495057 !important;
}

.btn-light-darker:focus {
  box-shadow: 0 0 0 0.2rem rgba(240, 240, 240, 0.5) !important;
}

.no-outline:focus {
  box-shadow: none !important;
}

/* Share tradies modal: gutter + stable columns (size slot always reserved) */
.tradie-share-checkbox-gutter {
  width: 2.75rem;
  flex-shrink: 0;
  box-sizing: border-box;
}

.tradie-share-user-header {
  min-height: 2.5rem;
  border-top: 1px solid grey;
  background-color: #006cff;
}

.tradie-share-file-row,
.tradie-share-personal-row,
.tradie-share-personal-subrow {
  border-top: 1px solid #d9d9d9;
}

.tradie-share-file-grid .tradie-share-file-name {
  flex: 1 1 0;
  min-width: 0;
}

.tradie-share-file-grid .tradie-share-file-status {
  flex: 0 0 8.85rem;
  min-width: 0;
}

.tradie-share-file-grid .tradie-share-file-size {
  flex: 0 0 4.9rem;
  min-width: 4.9rem;
  box-sizing: border-box;
}

.tradie-share-personal-fields .tradie-share-personal-key {
  flex: 0 0 42%;
  min-width: 0;
}

.tradie-share-personal-fields .tradie-share-personal-val {
  flex: 1 1 0;
  min-width: 0;
}

.project-approved-tick {
  vertical-align: -0.125em;
  font-size: 1rem;
}

/* Delete Project confirm: friendly, centred dialog with a danger icon and
   clear actions. Buttons stack full-width on phones so they're easy to tap. */
.delete-project-modal {
  padding: 0.5rem 0.25rem 0.25rem;
}

.delete-project-modal__icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #fdecea;
  color: #dc3545;
  font-size: 1.6rem;
}

.delete-project-modal__actions {
  display: flex;
  justify-content: center;
  gap: 0.75rem;
}

.delete-project-modal__btn {
  min-width: 130px;
  font-weight: 600;
}

@media (max-width: 575.98px) {
  .delete-project-modal__actions {
    flex-direction: column-reverse;
    gap: 0.5rem;
  }
  .delete-project-modal__btn {
    width: 100%;
    min-width: 0;
  }
}

/* Add Tradie modal footer: right-aligned pair on desktop, evenly-shared
   full-width pair on phones so they're easy to tap. */
.add-tradie-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 0.5rem 0.25rem 0;
}

.add-tradie-actions__btn {
  min-width: 130px;
  font-weight: 600;
  white-space: nowrap;
}

@media (max-width: 575.98px) {
  .add-tradie-actions {
    gap: 0.5rem;
  }
  /* Keep Cancel + Add Tradie on one line, sharing the width evenly — same
     height/padding/font-size as the other confirm-style button pairs
     across the app (Remove Resume, Add New Project, Purchase Token, etc). */
  .add-tradie-actions__btn {
    flex: 1 1 0;
    min-width: 0;
    height: 44px;
    padding: 4px 14px;
    font-size: 0.85rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.3rem;
  }
  /* The icon's own mr-1 would stack on top of the gap above — zero it out
     here so gap is the only thing spacing the icon from its label. */
  .add-tradie-actions__btn .b-icon {
    margin-right: 0 !important;
  }
}

/* Remove Tradie (from project) confirm modal: right-aligned pair on
   desktop, evenly-shared full-width pair on phones, matching the other
   confirm-style button pairs across the app. This div sits inside
   BootstrapVue's own #modal-footer slot (itself justify-content: flex-end),
   so width: 100% is needed — without it the div just hugs its own content
   and gets pushed right, leaving the buttons' flex-grow nothing to grow
   into. */
.remove-project-tradie-actions {
  display: flex;
  justify-content: flex-end;
  width: 100%;
  gap: 0.75rem;
}

.remove-project-tradie-btn {
  min-width: 110px;
  font-weight: 600;
}

.remove-project-tradie-text {
  margin: 0.75rem 0;
}

@media (max-width: 575.98px) {
  .remove-project-tradie-actions {
    justify-content: stretch;
    gap: 0.5rem;
  }
  .remove-project-tradie-btn {
    flex: 1 1 0;
    min-width: 0;
    height: 44px;
  }
  .remove-project-tradie-text {
    margin: 0.4rem 0 0.75rem;
    font-size: 0.9rem;
  }

  /* Tighten the surrounding dialog chrome — the default modal padding felt
     oversized for a two-line confirm prompt. */
  /deep/ .remove-project-tradie-modal {
    .modal-dialog {
      margin: 0.75rem;
    }
    .modal-header {
      padding-bottom: 0;
    }
    .modal-title {
      font-size: 1.15rem;
    }
    .modal-body {
      padding: 0 0.75rem;
    }
    .modal-footer {
      padding-top: 0.5rem;
    }
  }
}

/* Edit Project modal footer: Cancel + Update stay side-by-side on one line
   at every width (equal-width halves on phones). */
.edit-project-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 0.5rem;
}

.edit-project-actions__btn {
  min-width: 120px;
  font-weight: 600;
}

@media (max-width: 575.98px) {
  .edit-project-actions__btn {
    flex: 1 1 0;
    min-width: 0;
  }
}

/* Section labels (e.g. "Expiring Files (in 30 days):") stay on a single
   line on phones, where they get a full-width row of their own. From md up
   the label column is auto-width (col-md-auto) and sized to fit the text
   on one line anyway, so this mainly just guards the phone-width case. */
.title-nowrap-mobile {
  white-space: nowrap;
}
@media (min-width: 768px) {
  .title-nowrap-mobile {
    white-space: normal;
  }
}

/deep/ th.project-approved-th,
/deep/ td.project-approved-td {
  width: 28px;
  padding-left: 8px;
  padding-right: 0;
  text-align: center;
}

.project-name-detail-label {
  margin-right: 0.5rem;
  font-size: 1.2rem;
}

.project-name-detail-value {
  min-width: 0;
  font-size: 1.2rem;
}

/* Header action buttons (Delete / Edit / Back to Projects).
   Desktop keeps the original full-width-up-to-220px pill sizing. */
.header-action-btn {
  max-height: 64px;
  max-width: 220px;
  width: 100%;
  min-height: 46px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #c8d1dc !important;
  border-radius: 0.5rem;
  font-weight: 600;
  line-height: 1.2;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
  transition: background-color 0.18s ease, border-color 0.18s ease,
    box-shadow 0.18s ease, transform 0.18s ease;
}

.header-action-btn:hover,
.header-action-btn:focus {
  background-color: #f8fafc !important;
  border-color: #8794a3 !important;
  color: #1f2d3d !important;
  box-shadow: 0 4px 10px rgba(15, 23, 42, 0.1);
  transform: translateY(-1px);
}

.header-action-btn.text-danger:hover,
.header-action-btn.text-danger:focus {
  background-color: #fff5f5 !important;
  border-color: #dc3545 !important;
  color: #dc3545 !important;
}

.header-action-btn:focus {
  box-shadow: 0 0 0 0.2rem rgba(0, 108, 255, 0.14),
    0 4px 10px rgba(15, 23, 42, 0.1);
}

.header-action-btn:active {
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
  transform: translateY(0);
}

/* Small screens (< md): the three header buttons collapse to compact,
   icon-only buttons sitting side-by-side in a single right-aligned row.
   Labels reappear (reverting to the desktop pill buttons) from md up. */
@media (max-width: 767.98px) {
  .header-action-btn {
    width: auto;
    max-width: none;
    max-height: none;
    /* The global .btn rule fixes buttons at 56px tall — let these compact
       icon buttons size to their icon instead. */
    height: auto;
    min-height: 0;
    padding: 0.4rem 0.6rem;
    font-size: 0.875rem;
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .header-action-icon {
    font-size: 1.2rem;
    vertical-align: middle;
  }
}

/* Tablet range (md): the labelled pills are back but their md="4" columns get
   narrow — "Back to Projects" wraps taller than the 64px cap and the last line
   renders outside the border. Let the button grow to contain its text, and
   tighten font/padding so it usually fits on one line anyway. */
@media (min-width: 768px) and (max-width: 991.98px) {
  .header-action-btn {
    max-height: none;
    font-size: 0.875rem;
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }
}

/* Tradies toolbar action buttons (Add / Share / Download / Export).
   Desktop keeps the original 48px-tall row of buttons. */
.tradie-action-btn {
  height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  border-radius: 0.5rem;
  border-width: 1px;
  font-weight: 600;
  line-height: 1.2;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
  transition: background-color 0.18s ease, border-color 0.18s ease,
    box-shadow 0.18s ease, transform 0.18s ease;
}

.tradie-action-btn:hover,
.tradie-action-btn:focus {
  box-shadow: 0 4px 10px rgba(15, 23, 42, 0.14);
  transform: translateY(-1px);
}

.tradie-action-btn:focus {
  box-shadow: 0 0 0 0.2rem rgba(0, 108, 255, 0.16),
    0 4px 10px rgba(15, 23, 42, 0.14);
}

.tradie-action-btn:active {
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.1);
  transform: translateY(0);
}

.tradie-action-btn.btn-secondary {
  background-color: #697683;
  border-color: #697683;
}

.tradie-action-btn.btn-secondary:hover,
.tradie-action-btn.btn-secondary:focus {
  background-color: #56616d;
  border-color: #56616d;
}

/* Neutralise the toolbar row's default Bootstrap -15px gutter margins at
   every width, not just phones — its wrapping <b-container fluid> has that
   padding zeroed out (class="px-0"), so with nothing to cancel it out the
   raw negative margin pulled the search box ~15px left of the table below
   it (which sits one level up, outside that same negative margin). */
.tradie-toolbar-row {
  margin-left: 0 !important;
  margin-right: 0 !important;
}

/* Phones & small tablets: the four action buttons wrap into a tidy grid
   (two per row) with short labels, instead of a single overflowing row. */
@media (max-width: 767.98px) {
  .tradie-toolbar-actions {
    /* .tradie-search (below) pins its own inset to 15px !important so it
       lines up with the "Tradies:" label/table — this column had no such
       override and fell back to Bootstrap's plain column padding instead,
       leaving the button grid sitting further right/not stretching flush
       left the way the search box does. Match it explicitly.
       column-gap and row-gap are set separately (not the `gap` shorthand)
       so side-to-side spacing (Add/Share, Download/Export) and top-to-
       bottom spacing (between the two rows) can be tuned independently —
       row-gap is deliberately smaller than the old mb-3-driven 1rem gap
       between rows, per feedback that it felt like too much. */
    padding-left: 15px !important;
    padding-right: 15px !important;
    column-gap: 0.5rem;
    row-gap: 0.5rem;
  }
  .tradie-toolbar-actions .tradie-action-btn {
    flex: 1 1 calc(50% - 0.25rem);
    min-width: 0;
    height: 42px;
    /* Each button also carries the template's own mr-3/mb-3 (Bootstrap
       utilities, 1rem !important margin, meant for the single-row desktop
       layout) — zero both here so column-gap/row-gap (above) are the only
       things spacing the buttons apart; otherwise mr-3/mb-3 stack on top of
       them and the row overflows/gets more vertical space than intended. */
    margin: 0 !important;
    padding-left: 0.5rem !important;
    padding-right: 0.5rem !important;
    font-size: 0.78rem;
    /* Icon + label were laid out as plain inline content, so how balanced
       they looked depended on each label's own length/whitespace — "Add"
       had room to breathe while "Download" (the longest label) read as
       cramped against its icon in the same fixed-width cell. Flex-centring
       with an explicit gap gives all four buttons identical, predictable
       icon-to-label spacing regardless of label length. */
    display: flex !important;
    align-items: center;
    justify-content: center;
    gap: 0.3rem;
  }

  /* Keep the leading +/download icon from crowding out the label. */
  .tradie-toolbar-actions .tradie-action-btn .b-icon {
    font-size: 0.9rem !important;
    flex-shrink: 0;
  }

  /* Supervisor tradies only see the Download button, and the 50% grid cell
     above stretches a lone button into a full-width slab — let it size to
     its label and sit on the left, aligned with the search field, instead
     of floating alone on the right. Business admins (2-4 buttons) keep the
     right-aligned grid. */
  .tradie-toolbar-actions--supervisor {
    justify-content: flex-start !important;
  }
  .tradie-toolbar-actions--supervisor .tradie-action-btn {
    flex: 0 0 auto;
    padding-left: 1rem !important;
    padding-right: 1rem !important;
    /* .tradie-toolbar-actions now pins its own padding-left to match
       .tradie-search exactly, so the button's left edge already lands on
       the search field's — no extra pull needed here. */
  }

  /* Compact the search field so it matches the smaller buttons, and pin its
     left/right gutter so the label + input line up with the "Tradies:" label,
     the buttons and the table (rather than sitting ~15px further left). */
  .tradie-search {
    padding-left: 15px !important;
    padding-right: 15px !important;
  }
  .tradie-search b {
    font-size: 0.85rem;
  }
  .tradie-search /deep/ .form-control {
    height: 40px;
    padding: 0.25rem 0.6rem;
    font-size: 0.85rem;
  }

  /* Needing Attention/Expired Requests' search boxes, nudged to land under
     the table's row text (same tuning as the desktop rule below). */
  .band-search {
    padding-left: 15px !important;
  }
}

/* Tablet (md, 768-991px): give the search block clearance from the
   "Tradies:" label column and reclaim the pr-md-5 dead space so its label
   wraps less. */
@media (min-width: 768px) and (max-width: 991.98px) {
  .tradie-search {
    padding-right: 1rem !important;
  }
}

/* Phones: the title and header-action columns split the row 50/50, leaving
   the project name only half the width so even short names wrapped to two
   lines. Let the title take the free space (actions size to content) and
   keep it on a single line, ellipsizing very long names. */
@media (max-width: 767.98px) {
  /* Centre the back/edit/delete icons on the title line. */
  .project-header-row {
    align-items: center;
  }
  .project-title-col {
    /* Keep bootstrap's grow-from-zero basis (basis:auto would defer to the
       col's width:100% and push the actions onto a second line); just allow
       the title to shrink below its content width so it can ellipsize. */
    min-width: 0;
  }
  .project-actions-col {
    flex: 0 0 auto;
    width: auto;
  }
  .project-title {
    font-size: 1.5rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-bottom: 0;
  }
}

/* Between the phone grid and the truly-wide desktop, the four action buttons
   would wrap 3-on-top-of-1 (short labels ~830px, full labels up to ~1450px).
   Force the same tidy 2x2 grid as phones across that whole span, so the
   layout goes 2x2 -> all-in-one-row with nothing in between. */
@media (min-width: 768px) and (max-width: 1449.98px) {
  .tradie-toolbar-actions .tradie-action-btn {
    /* mr-3 (1rem) is part of each 50% cell, leaving room for exactly two. */
    flex: 1 1 calc(50% - 1rem);
    max-width: calc(50% - 1rem);
    min-width: 0;
  }
}

/* ------------------------------------------------------------------ *
 * Tradies table: compact + horizontally scrollable on small screens.
 *
 * Two complementary strategies keep the table usable down to 320px:
 *   1. Columns are progressively hidden via d-none/d-*-table-cell in the
 *      `tradieFields` computed (only the essentials show on phones, the
 *      rest reappear as the viewport grows back into the desktop table).
 *   2. Whatever columns ARE shown never wrap into tall, cramped rows —
 *      cells stay on a single line and the `.table-responsive` wrapper
 *      (from b-table's `responsive` prop) scrolls horizontally instead.
 * The desktop table (>= lg) is left untouched.
 * ------------------------------------------------------------------ */

/* Below the desktop breakpoint, prefer a tidy horizontal scroll over
   ugly wrapping. Momentum scrolling + a slim scrollbar on touch/tablet. */
@media (max-width: 991.98px) {
  /deep/ .table-responsive {
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
  }
  /deep/ .tradies-table th,
  /deep/ .tradies-table td {
    white-space: nowrap;
  }
  /* Let the (widest) name column wrap instead of forcing a horizontal
     scroll from long names — keeps the table compact on phones. */
  /deep/ .tradies-table td.tradie-name-cell {
    white-space: normal;
    min-width: 5.5rem;
  }
  /* The lock banner is the one element allowed to wrap within its row. */
  /deep/ .tradies-table tr.row-locked .lock-overlay,
  /deep/ .tradies-table tr.row-locked .lock-overlay span {
    white-space: normal;
  }
}

/* From the md desktop table (768px) up to ~1400px the tradie tables are tight
   in their card — the Needing Attention band also carries an extra "Reason"
   column. Shrink font + padding and let cells WRAP so every column stays
   visible without a horizontal scrollbar. This block is declared after the
   `max-width: 991.98px` rules so its `white-space: normal` wins over their
   nowrap-and-scroll in the overlapping 768–992 range. */
@media (min-width: 768px) and (max-width: 1399.98px) {
  /deep/ .tradies-table {
    font-size: 0.72rem;
  }
  /deep/ .tradies-table th,
  /deep/ .tradies-table td {
    padding: 0.3rem 0.3rem;
    white-space: normal;
    /* Long unbroken tokens (emails) wrap instead of forcing the table wide. */
    overflow-wrap: anywhere;
  }
  /deep/ .attention-reason-cell {
    max-width: 200px;
  }
  .attention-reason {
    font-size: 0.68rem;
    line-height: 1.15;
  }
}

@media (max-width: 575.98px) {
  /deep/ .tradies-table {
    font-size: 0.78rem;
  }
  /deep/ .tradies-table th,
  /deep/ .tradies-table td {
    padding: 0.4rem 0.35rem;
  }

  /* Confine the "Locked - Assign token" banner to the row so it can't
     spill past the viewport (the JS centring is overridden here). */
  /deep/ .tradies-table tr.row-locked .lock-overlay {
    left: 0 !important;
    right: 0 !important;
    top: 50% !important;
    width: auto !important;
    max-width: 100% !important;
    transform: translateY(-50%) !important;
    white-space: normal !important;
    justify-content: center;
    text-align: center;
    padding: 0 6px;
    font-size: 0.85rem;
  }
  /deep/ .tradies-table tr.row-locked .lock-overlay span {
    display: none;
  }
  /deep/ .tradies-table tr.row-locked .lock-overlay .lock-icon {
    margin-right: 0 !important;
    width: 20px;
    height: 20px;
  }

  /* Sizing now lives on .tradies-pagination (below, shared with
     ExpiringTradieList.vue's own copy of this rule) so all four tables'
     pagination is guaranteed to read the exact same size on phones,
     regardless of any other rule that might otherwise apply. */
  .tradies-pagination {
    font-size: 0.85rem !important;
  }
  .tradies-pagination /deep/ .page-link {
    padding: 0.25rem 0.5rem !important;
  }

  /* Shrink the "Showing X out of total of Y" summary line — matches
     ExpiringTradieList.vue's own copy of this rule/class (that one had no
     size override at all before, reading noticeably larger than these
     three), so all four read the same size. */
  .tradie-count {
    font-size: 0.8rem;
    margin-top: 0.35rem;
    margin-bottom: 0.5rem;
  }
}

/* ------------------------------------------------------------------ *
 * Mobile tradie rows (< md): stacked "card" rows matching the projects
 * list pattern — a bold name, a small meta line, inline actions, and a
 * divider — instead of a scrolling table. Hidden on desktop via
 * `d-md-none`, so these styles never touch the >= md table.
 * ------------------------------------------------------------------ */
/* Inter-section divider — visible horizontal rule with breathing room. */
.section-separator {
  height: 0;
  border: 0;
  border-top: 1px solid #e9ecef;
  margin: 2rem 0;
}

.mobile-tradie-list {
  margin-top: 0.5rem;
}

/* Explicit, consistent gap above the pagination on all three tables here —
   matches Expiring Files' own .tradies-pagination rule in
   ExpiringTradieList.vue, so all four read the same instead of depending on
   each list's own incidental bottom padding/margin. */
.tradies-pagination {
  margin-top: 0.5rem;
}

/* On desktop (md+), indent the four sections so their labels align with the
   "Project" label above them in the card body. */
@media (min-width: 768px) {
  .attention-row,
  .mobile-flush-row,
  .tradie-row {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  /* Expiring Files' content is <expiring-tradie-list>, which wraps itself
     in its own <b-container fluid> — Bootstrap's default 0.9375rem gutter
     on top of the 1rem above. Needing Attention/Expired Requests render
     their table directly with no such wrapper, and Tradies' own
     <b-container fluid> has that gutter zeroed out (class="px-0"), so
     without this they all end up narrower-looking (less side padding) than
     Expiring Files even though the row itself has identical padding. Match
     it directly instead of touching each one's own container. */
  .attention-cards-col,
  .expired-requests-content-col,
  .tradie-content-col {
    padding-left: 0.9375rem;
    padding-right: 0.9375rem;
  }

  /* Needing Attention/Expired Requests' search boxes, nudged to land under
     "Full Name". */
  .band-search {
    padding-left: 15px !important;
  }
}

/* On phones, pull the "Needing Attention" yellow band's inner content close
   to its edges so the cards fill the width instead of leaving a wide gap. */
@media (max-width: 767.98px) {
  /* Extend all three bands outward, leaving a small EVEN gap on both sides.
     .attention-row used to be right-heavy (-1.75rem) to compensate for the
     surrounding card-body's own left/right padding being asymmetric (pl-0
     with no matching pr-0) — now that that's fixed at the source, all three
     can share the same even pull. .tradie-row previously had no mobile
     override at all, so it kept Bootstrap's default -15px row gutter
     uncancelled (nothing here zeroes a matching amount of padding back in
     the way the desktop-only .tradie-row/.tradie-content-col rule does at
     md+) — widening the row past the viewport at 320px. */
  .attention-row,
  .mobile-flush-row,
  .tradie-row {
    margin-left: -0.5rem;
    margin-right: -0.5rem;
  }
  .attention-row > [class*='col'],
  .mobile-flush-row > [class*='col'],
  .tradie-row > [class*='col'] {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }
  /* Section labels ("Expiring Files (in 30 days):" etc.) are .col-12 on
     phones, so the global 30px col-12 margin opens a big gap between the
     label and its section content — collapse it. */
  .mobile-flush-row > .title-nowrap-mobile {
    margin-bottom: 0;
  }
  .attention-cards-col .mobile-tradie-list {
    margin-top: 0.25rem;
  }

  /* Shrink the inter-section separators on phones — keep the border visible
     but pull the surrounding margin in so it doesn't feel too airy. */
  .section-separator {
    margin: 1rem 0;
  }
  .mobile-tradie-list {
    margin-top: 0.25rem;
  }
  .mobile-tradie-list .py-4 {
    padding-top: 0.75rem !important;
    padding-bottom: 0.75rem !important;
  }
  .tradie-count {
    margin-top: 0.25rem !important;
  }

  /* Tighten every stacked tradie row so name + email fit down to 320px. */
  .mobile-tradie-item {
    gap: 0.25rem;
    padding: 0.6rem 0.4rem 0.6rem 0.55rem;
  }
  .mobile-tradie__select-btn,
  .mobile-tradie__remove-btn {
    height: 28px;
    width: 28px;
  }
  /* The Needing Attention band mirrors its desktop table, which has no Select
     column — drop the select checkbox from its mobile rows too so the narrow
     band isn't three controls wide. */
  .attention-cards-col .mobile-tradie__select {
    display: none;
  }
}

.mobile-tradie-item {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.65rem 0.6rem 0.65rem 0.85rem;
  border-bottom: 1px solid #e1e6ea;
  background: #fff;
}

.mobile-tradie-item.row-locked {
  background: #f5f5f5;
}

.mobile-tradie-item.is-selected {
  background: #eef9f1;
}

.mobile-tradie__tick {
  flex-shrink: 0;
  font-size: 1rem;
}

.mobile-tradie__body {
  flex: 1 1 auto;
  min-width: 0;
}

// Name + the "project approved" tick sit on one line, tick to the right of
// the (truncated) name — keeps every row's name flush-left whether approved
// or not, instead of the tick being a column that shifts the name across.
.mobile-tradie__name-row {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  min-width: 0;
}
.mobile-tradie__name-row .mobile-tradie__name {
  flex: 0 1 auto;
}

.mobile-tradie__name {
  display: block;
  font-weight: 600;
  font-size: 0.875rem;
  color: #364052;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mobile-tradie__meta {
  font-size: 0.72rem;
  color: #8b95a3;
  margin-top: 0.15rem;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

// The locked message ("Locked - Contact your business admin to unlock") is
// longer than one line at 320px; unlike the email/trades meta it can't be
// meaningfully truncated, so let it wrap instead of clipping.
.mobile-tradie__meta--lock {
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
}

// Needing Attention band, mobile rows: the reasons list is the point of the
// band, so show it in full (wrapping) rather than clipped on the meta line.
.mobile-tradie__reasons {
  margin: 0.25rem 0 0;
  padding-left: 1.05em;
  list-style: disc outside;
  font-size: 0.72rem;
  line-height: 1.3;
  color: #c5221f;
}
.mobile-tradie__reasons li {
  white-space: normal;
}

.mobile-tradie__lock-icon {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}

.mobile-tradie__select,
.mobile-tradie__remove {
  flex-shrink: 0;
}

.mobile-tradie__select-btn,
.mobile-tradie__remove-btn {
  height: 32px;
  width: 32px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.mobile-tradie__select-btn {
  border-radius: 8px;
  border: 1px solid grey;
}
</style>
